import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClientWorkGuard } from './post-creator/useClientWorkGuard'
import { useMediaUploadQueue, hasUnfinishedMediaUploads, restoreUploadPlaceholder } from './post-creator/useMediaUploadQueue'
import { summarizeFindings } from './moderation/moderationFindings'
import { postGroupSaveMessage } from './moderation/postGroupModerationModel'
import { Link, useNavigate, useParams } from 'react-router'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { formatOdds } from '@feed-widget/utils/odds'
import { selectionPresentation } from '@feed-widget/utils/selectionSet'
import { commandCenterApi } from '../services/api'
import { extractErrorMessage } from '../lib/useApiMutation'
import { getPreviewSelections } from '../utils/feedPreviewItem'
import {
  Button,
  ErrorSummary,
  FormField,
  FormSection,
  PageLayout,
  StatePanel,
  Tabs,
  Toggle,
  SegmentedControl,
} from './common'
import { DecisionReview } from './patterns'
import AdvancedSettingsSection from './post-creator/AdvancedSettingsSection'
import BetSection from './post-creator/BetSection'
import ContentSection from './post-creator/ContentSection'
import CtaSection from './post-creator/CtaSection'
import MediaSection from './post-creator/MediaSection'
import { DEFAULT_AI_IMAGE_SIZE } from './post-creator/imagePrompt'
import { imageGenerationErrorMessage, restoreImageGenerationJob } from './post-creator/imageGenerationJob'
import { useImageGenerationJob } from './post-creator/useImageGenerationJob'
import { useReferenceImages } from './post-creator/useReferenceImages'
import PostPreviewPanel from './post-creator/PostPreviewPanel'
import { PUBLISH_TO_DESCRIPTION, PUBLISH_TO_TITLE } from './post-creator/PublishToSection'
import RegulationTargetSection from './post-creator/RegulationTargetSection'
import SurfacePicker from './post-creator/SurfacePicker'
import TagChipPicker from './post-creator/TagChipPicker'
import SuggestedTagChips from './post-creator/SuggestedTagChips'
import { matchSuggestedTagIds } from './post-creator/tagSuggestions'
import PostComposerLayout from './post-creator/PostComposerLayout'
import OrgMediaOverridePicker from './command-center/OrgMediaOverridePicker'
import {
  aiUnavailableMessage,
  deriveAiAvailability,
  imageGenerationCapabilitiesFor,
  retryAiCapabilities,
} from './command-center/aiAvailability'
import {
  attachAssetToState,
  createUnavailableVideoAnalysis,
  describeOrphanedMediaOwners,
  findOrphanedMediaOwners,
  mediaUploadContentType,
  createVideoAnalysisFromError,
  createVideoAnalysisFromJob,
  isRetryableVideoAnalysis,
  readMediaOwner,
  removeMediaAtIndex,
} from './post-creator/mediaAttachments'
import {
  blockImplicitFormSubmit,
  parseAiJson,
  parseStoredJson,
  persistStoredJson,
  removeStoredValue,
  useBlobPreviewUrlCleanup,
} from './post-creator/composerShared'
import { buildRegulationLookup, formatRegulationName } from '../utils/regulationLabels'
import { parseOutcomeId } from '../utils/odds'
import {
  buildCommandCenterPostGroupPayloadV2,
  COMMAND_CENTER_COMPOSER_STEPS,
  COMMAND_CENTER_SECTION_REGISTRY,
  createCommandCenterComposerState,
  resolveCommandCenterDestinationDocumentV2,
  normalizeCommandCenterComposerState,
  validateCommandCenterComposerStep,
} from './CommandCenterPostComposerModel'
import { renderComposerSections, sectionLabel } from './post-creator/postComposerSections'
import {
  applyValidatedDefaultWagerType,
  buildAuthoredWagerPreviewSelectionSet,
  createComposerAdjunctState,
  createComposerAiReview,
  formatDateTimeInput,
  isComposerAdjunctInProgress,
  normalizeComposerAiReview,
  normalizeComposerReferenceImages,
  reconcileAuthoredWagerEntries,
  useComposerDocument,
  validateAuthoredWagerEntries,
} from './post-creator/postComposerModel'
import { kambiOutcomePreviewLeg } from './post-creator/kambiOutcomePreview'
import {
  commandCenterLegSearchOfferings,
  commandCenterLegSearchScope,
  createCommandCenterLegSearch,
} from './commandCenterLegSearch'
import composerStyles from './post-creator/PostComposer.module.css'
import styles from './CommandCenterPostCreator.module.css'

const DRAFT_STORAGE_PREFIX = 'command-center-post-draft-v2'
const PREFERENCES_STORAGE_KEY = 'command-center-post-composer-prefs-v1'
const DEFAULT_ADVANCED_SECTION_EXPANDED = false
const RETIRED_PROVIDER_ADAPTERS = new Set(['cryptodotcom', 'og'])
const EMPTY_ARRAY = []
const TRANSCRIPTION_TIMEOUT_MS = 13 * 60 * 1000
const AI_CAPABILITIES_STALE_TIME_MS = 60_000
// Reference images Command Center AI can send; a brand's are not its own.
const COMMAND_CENTER_REFERENCE_SCOPE = 'command-center'

const AI_CAPABILITIES_QUERY_OPTIONS = {
  queryKey: ['cc-post-ai-capabilities'],
  queryFn: () => commandCenterApi.getAiCapabilities(),
  staleTime: AI_CAPABILITIES_STALE_TIME_MS,
  retry: retryAiCapabilities,
}

function mergeTranscriptionOutput(currentValue, result = {}) {
  return {
    ...currentValue,
    transcript: result?.transcript || currentValue.transcript,
    caption: result?.caption || currentValue.caption,
    captions: Array.isArray(result?.captions)
      ? result.captions
      : result?.caption ? [result.caption] : currentValue.captions,
    tags: Array.isArray(result?.tags) ? result.tags : currentValue.tags,
    betSummary: result?.summary || currentValue.betSummary,
  }
}

function createEmptyFormData() {
  return createCommandCenterComposerState()
}

function normalizeSelectedCreator(creator) {
  if (!creator) return null
  return {
    id: creator.id || creator.value || '',
    display_name: creator.display_name || creator.label || creator.id || '',
    avatar_url: creator.avatar_url || '',
    allowed_offerings: creator.allowed_offerings || [],
  }
}

function normalizeCommandCenterOrganization(organization = {}) {
  const mappings = Array.isArray(organization.regulation_mappings)
    ? organization.regulation_mappings
    : Array.isArray(organization.mappings) ? organization.mappings : []
  const offeringIds = [
    ...(Array.isArray(organization.kambi_offering_ids) ? organization.kambi_offering_ids : []),
    ...(Array.isArray(organization.offering_ids) ? organization.offering_ids : []),
    ...mappings.map((mapping) => mapping?.offering_id),
  ].map((value) => String(value || '').trim()).filter(Boolean)
  return {
    ...organization,
    kambi_offering_ids: [...new Set(offeringIds)],
    mappings,
  }
}

function regulationLabelForOrganization(organization, offeringId) {
  const mapped = buildRegulationLookup(organization?.mappings)(offeringId)
  return mapped || formatRegulationName(offeringId) || offeringId
}

function kambiRegulationAvailabilityError(validation, organizations, targetOfferingsMap, outcomeIds) {
  if (!validation?.outcomes || outcomeIds.length === 0) return ''
  let unavailableCount = 0
  const unavailableByOrganization = organizations.map((organization) => {
    const unavailable = (targetOfferingsMap[organization.id] || []).filter((offeringId) => (
      outcomeIds.some((outcomeId) => validation.outcomes[String(outcomeId)]?.availability?.[offeringId] !== true)
    ))
    unavailableCount += unavailable.length
    return unavailable.length === 0
      ? null
      : `${organization.name}: ${unavailable.map((offeringId) => regulationLabelForOrganization(organization, offeringId)).join(', ')}`
  }).filter(Boolean)
  if (unavailableByOrganization.length === 0) return ''
  return `Selected market is unavailable for ${unavailableByOrganization.join('; ')}. Deselect ${unavailableCount === 1 ? 'that regulation' : 'those regulations'} or choose a market available to every selected regulation.`
}

function pruneTargetMap(targetMap, selectedOrganizationIds, allowedValuesForOrganization) {
  const selected = new Set(selectedOrganizationIds)
  let changed = false
  const next = {}
  Object.entries(targetMap || {}).forEach(([organizationId, values]) => {
    if (!selected.has(organizationId)) {
      changed = true
      return
    }
    const allowed = allowedValuesForOrganization(organizationId)
    const currentValues = Array.isArray(values) ? values : []
    const pruned = allowed === null
      ? currentValues
      : currentValues.filter((value) => allowed.has(value))
    if (!Array.isArray(values) || pruned.length !== currentValues.length) changed = true
    next[organizationId] = pruned
  })
  return changed ? next : targetMap
}

function providerContractLabel(selection, preferContractTitle = false) {
  const meta = selection?.selection_metadata || {}
  if (preferContractTitle) {
    return selection?.name || meta.contract_title || meta.team_abbrev || meta.team_name || meta.individual_name || 'Contract'
  }
  return meta.team_abbrev || meta.team_name || meta.individual_name || selection?.name || 'Contract'
}

function createUploadedMediaFromStoredMedia(storedMedia = []) {
  return (storedMedia || [])
    .filter(Boolean)
    .map((media) => !media.url && !media.mediaId ? restoreUploadPlaceholder(media) : ({
      id: media.mediaObjectId || media.id || crypto.randomUUID(),
      mediaObjectId: media.mediaObjectId || media.id || null,
      type: media.type,
      name: media.name || 'media',
      mediaId: media.mediaId || media.media_id || null,
      objectKey: media.objectKey || media.object_key,
      sourceObjectKey: media.sourceObjectKey || media.source_object_key,
      previewUrl: media.url || media.previewUrl,
      url: media.url || media.previewUrl,
      uploading: false,
      ...(media.uploadPhase ? { uploadPhase: 'ready', uploadSize: media.uploadSize } : {}),
      // Owner org is draft-only UI state. Group documents are source-free, so
      // media rehydrated from a saved group is intentionally unowned rather
      // than attributed by guesswork.
      ...(readMediaOwner(media) || {}),
    }))
}

function normalizeDraftTranscriptionJobs(savedJobs, uploadedMedia = []) {
  const records = Array.isArray(savedJobs)
    ? savedJobs
    : Object.entries(savedJobs || {}).map(([mediaId, entry]) => ({ ...entry, mediaId }))
  const mediaById = new Map(uploadedMedia.map((media) => [media.id, media]))
  const validStatuses = new Set([
    'polling',
    'retrying',
    'completed',
    'unavailable',
    'invalid_input',
    'failed_retryable',
    'too_large',
  ])

  return records.reduce((jobs, entry) => {
    if (!entry?.mediaId || !mediaById.has(entry.mediaId) || !validStatuses.has(entry.status)) return jobs
    const media = mediaById.get(entry.mediaId)
    const parsedStartedAt = typeof entry.startedAt === 'number'
      ? entry.startedAt
      : Date.parse(entry.startedAt || '')
    jobs[entry.mediaId] = {
      jobId: entry.jobId || null,
      // Only a job saved before Command Center had its own AI key has one.
      ...(entry.orgSlug ? { orgSlug: entry.orgSlug } : {}),
      status: entry.status,
      error: entry.error || '',
      errorCode: entry.errorCode || null,
      retryable: entry.retryable === true,
      attempt: entry.attempt || 1,
      retryOf: entry.retryOf || null,
      mediaUrl: entry.mediaUrl || media.url || media.previewUrl || '',
      title: entry.title || media.name || 'video',
      contentType: entry.contentType || 'video/mp4',
      startedAt: Number.isNaN(parsedStartedAt) ? Date.now() : parsedStartedAt,
      completedAt: entry.completedAt || null,
    }
    return jobs
  }, {})
}

function buildDraftKey(mode, groupId) {
  return `${DRAFT_STORAGE_PREFIX}:${mode}:${groupId || 'new'}`
}

function hasComposerContent(formData, uploadedMedia, selectedProviderId, selectedOrgIds, selectedCreator, adjunct = createComposerAdjunctState()) {
  return Boolean(
    selectedProviderId ||
    selectedOrgIds.length ||
    selectedCreator?.id ||
    formData.label.trim() ||
    formData.text.trim() ||
    formData.kambiOutcomeIds.length ||
    formData.providerSelectionIds.length ||
    Object.values(formData.targetOfferingsMap).some((ids) => ids.length) ||
    Object.values(formData.tagIdsMap).some((ids) => ids.length) ||
    Object.keys(formData.surfaceIdsMap).length ||
    formData.mediaObjects.length ||
    uploadedMedia.length ||
    formData.ctaKind !== 'betslip' ||
    formData.isHidden ||
    formData.isPinned ||
    formData.boostFactor !== 1 ||
    formData.postType !== 'organic' ||
    formData.skipApproval ||
    isComposerAdjunctInProgress(adjunct)
  )
}

function normalizeCanonicalMedia(mediaObjects = []) {
  // Go nil slices marshal to JSON `null` rather than `[]`, so the default
  // parameter alone (which only covers `undefined`) is not enough.
  return (mediaObjects || []).map((media) => ({
    ...media,
    id: media.id,
    mediaObjectId: media.id,
    mediaId: media.mediaId || media.asset_id || media.id || null,
    object_key: media.object_key,
    source_object_key: media.source_object_key,
    type: media.type,
    url: media.url,
    name: media.name || 'media',
  }))
}

function normalizeCanonicalDestinationOverrides(destinations = []) {
  return Object.fromEntries(destinations.map((destination) => {
    const overrides = { ...(destination?.overrides || {}) }
    if (overrides.media_objects && Array.isArray(overrides.media_objects.value)) {
      overrides.media_objects = {
        value: normalizeCanonicalMedia(overrides.media_objects.value),
      }
    }
    return [destination.organization_id, overrides]
  }))
}

function createComposerStateFromGroup(group) {
  const shared = group?.document?.shared || {}
  const market = shared.market || {}
  const advanced = shared.advanced || {}
  const destinations = group?.document?.destinations || []
  return {
    ...createEmptyFormData(),
    label: shared.label || '',
    text: shared.text || '',
    title: shared.title || '',
    subtitle: shared.subtitle || '',
    ctaKind: shared.cta?.kind || 'none',
    ctaLabel: shared.cta?.label || '',
    ctaTarget: shared.cta?.target || '',
    mediaObjects: normalizeCanonicalMedia(shared.media_objects),
    kambiOutcomeIds: market.kambi_outcome_ids || [],
    wagerEntries: market.wager_entries || [],
    providerSelectionIds: market.provider_selection_ids || [],
    tagIdsMap: Object.fromEntries(destinations.map((destination) => [
      destination.organization_id,
      destination.distribution?.tag_ids || [],
    ])),
    targetOfferingsMap: Object.fromEntries(destinations.map((destination) => [
      destination.organization_id,
      destination.distribution?.regulation_ids || [],
    ])),
    surfaceIdsMap: Object.fromEntries(destinations.map((destination) => [
      destination.organization_id,
      destination.distribution?.surface_ids || [],
    ])),
    bookOverrides: normalizeCanonicalDestinationOverrides(destinations),
    postType: advanced.post_type || 'organic',
    expiresAt: formatDateTimeInput(advanced.expires_at),
    scheduledFor: formatDateTimeInput(advanced.scheduled_for),
    promoPriority: advanced.promo_priority ?? 0,
    interleaveEvery: advanced.interleave_every ? String(advanced.interleave_every) : '',
    placementMode: advanced.interleave_every ? 'interleave' : 'pinned',
    shareEnabled: advanced.share_enabled !== false,
    copyCountEnabled: Boolean(advanced.copy_count_enabled),
    cardPresentation: advanced.card_presentation || null,
    skipApproval: Boolean(advanced.skip_approval),
  }
}

function CommandCenterPostCreator({ mode = 'create' }) {
  const navigate = useNavigate()
  const { id: groupIdParam } = useParams()
  const queryClient = useQueryClient()
  const isEditMode = mode === 'edit' && !!groupIdParam
  const draftKey = useMemo(() => buildDraftKey(mode, groupIdParam), [groupIdParam, mode])
  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID())

  const [selectedProviderId, setSelectedProviderId] = useState('')
  const [selectedOrgIds, setSelectedOrgIds] = useState([])
  const [loadedGroupOrgs, setLoadedGroupOrgs] = useState([])
  const [loadedGroupBaseline, setLoadedGroupBaseline] = useState(null)
  const [selectedCreator, setSelectedCreator] = useState(null)
  const [creatorSearch, setCreatorSearch] = useState('')
  const [marketSearchQuery, setMarketSearchQuery] = useState('')
  const {
    authored: formData,
    adjunct,
    setAuthored: setFormData,
    setAdjunctField,
  } = useComposerDocument({
    createAuthored: createEmptyFormData,
    normalizeAuthored: normalizeCommandCenterComposerState,
  })
  const [uploadedMedia, setUploadedMedia] = useState([])
  const mediaUploads = useMediaUploadQueue(setUploadedMedia)
  const [kambiValidation, setKambiValidation] = useState(null)
  const [providerSelectionDetails, setProviderSelectionDetails] = useState([])
  const [orgTags, setOrgTags] = useState({})
  const [orgSurfaces, setOrgSurfaces] = useState({})
  const [destinationMetadata, setDestinationMetadata] = useState({
    key: '',
    loading: false,
    errors: [],
    // Books whose saved-tag read failed: their orgTags entry is empty only
    // because the read failed, not because they have no tags.
    failedTagOrganizationIds: [],
  })
  const [destinationMetadataRetry, setDestinationMetadataRetry] = useState(0)
  const [previewOrganizationId, setPreviewOrganizationId] = useState('')
  const [assetSearch, setAssetSearch] = useState('')
  const [tagSuggestionsRevision, setTagSuggestionsRevision] = useState(0)
  const { aiReview, imagePrompt, referenceImages, imageGenerationJob, transcriptionJobs, generatedAssetIds } = adjunct
  const setImagePrompt = useCallback((value) => setAdjunctField('imagePrompt', value), [setAdjunctField])
  const setReferenceImages = useCallback((value) => setAdjunctField('referenceImages', value), [setAdjunctField])
  const setImageGenerationJob = useCallback((value) => setAdjunctField('imageGenerationJob', value), [setAdjunctField])
  const setAiReview = useCallback((value) => setAdjunctField('aiReview', value), [setAdjunctField])
  const [captionGuidance, setCaptionGuidance] = useState('')
  const setTranscriptionJobs = useCallback((value) => setAdjunctField('transcriptionJobs', value), [setAdjunctField])
  const setGeneratedAssetIds = useCallback((value) => setAdjunctField('generatedAssetIds', value), [setAdjunctField])
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false)
  const [advancedSectionExpanded, setAdvancedSectionExpanded] = useState(DEFAULT_ADVANCED_SECTION_EXPANDED)
  const [pendingDraft, setPendingDraft] = useState(null)
  const [draftReady, setDraftReady] = useState(false)
  const [loading, setLoading] = useState(isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPreflighting, setIsPreflighting] = useState(false)
  const [preflightState, setPreflightState] = useState(null)
  const [activeStep, setActiveStep] = useState('content')
  const [composerMode, setComposerMode] = useState(() => {
    const saved = parseStoredJson(PREFERENCES_STORAGE_KEY, {})
    return saved?.composerMode === 'guided' ? 'guided' : 'one-page'
  })
  const [reviewExpanded, setReviewExpanded] = useState(false)
  const [onePageBetExpanded, setOnePageBetExpanded] = useState(false)
  // Which book's destination pane is open. Pure view state: it never reaches
  // formData, so it cannot leak into the serialized document.
  const [activeDestinationOrgId, setActiveDestinationOrgId] = useState('')
  const [stepError, setStepError] = useState(null)
  const [revisionConflict, setRevisionConflict] = useState(null)
  const [isComparingRevision, setIsComparingRevision] = useState(false)
  useEffect(() => {
    if (formData.kambiOutcomeIds.length > 0 || formData.providerSelectionIds.length > 0) {
      setOnePageBetExpanded(true)
    }
  }, [formData.kambiOutcomeIds.length, formData.providerSelectionIds.length])
  const errorSummaryRef = useRef(null)
  const mobilePreviewTriggerRef = useRef(null)
  const removedUploadPlaceholderIdsRef = useRef(new Set())
  const retryingTranscriptionMediaIdsRef = useRef(new Set())

  const providersQuery = useQuery({
    queryKey: ['cc-providers'],
    queryFn: () => commandCenterApi.listProviders(),
  })
  const providers = providersQuery.data?.providers || EMPTY_ARRAY
  const visibleProviders = providers.filter((provider) => !RETIRED_PROVIDER_ADAPTERS.has(provider.ingest_adapter) || provider.id === selectedProviderId)
  const selectedProvider = providers.find((provider) => provider.id === selectedProviderId)
  const isKambi = selectedProvider?.ingest_adapter === 'kambi'
  const isCryptoDotCom = selectedProvider?.ingest_adapter === 'cryptodotcom'
  const isOG = selectedProvider?.ingest_adapter === 'og'
  const isPredictionContractProvider = isCryptoDotCom || isOG
  const authoredWagerValidationError = isKambi
    ? validateAuthoredWagerEntries(formData.wagerEntries, formData.kambiOutcomeIds, kambiValidation?.outcomes)
    : ''
  // Mirrors kambiDestinations' filter below without depending on it (that
  // memo needs selectedOrgs, computed further down): once every selected
  // organization has zero selected regulations there is nothing left to
  // validate, so this must not report "pending" forever -- preflight's
  // per-destination regulation_required check is what surfaces that state.
  const hasKambiValidationScope = selectedOrgIds.some((organizationId) => (
    (formData.targetOfferingsMap[organizationId] || EMPTY_ARRAY).length > 0
  ))
  const pendingKambiValidationError = isKambi && formData.kambiOutcomeIds.length > 0 && hasKambiValidationScope && !kambiValidation
    ? 'Wait for provider validation to finish, or change the selected market.'
    : ''

  const organizationsQuery = useQuery({
    queryKey: ['cc-organizations'],
    queryFn: () => commandCenterApi.listOrganizations(),
  })
  const allOrganizations = useMemo(
    () => (organizationsQuery.data?.organizations || EMPTY_ARRAY).map(normalizeCommandCenterOrganization),
    [organizationsQuery.data?.organizations],
  )
  const availableOrgs = useMemo(
    () => allOrganizations.filter((organization) => (
      organization.provider_id === selectedProviderId
      || (!organization.provider_id && providers.length === 1)
    )),
    [allOrganizations, providers.length, selectedProviderId],
  )
  const organizationsByProvider = useMemo(() => {
    const grouped = new Map(visibleProviders.map((provider) => [provider.id, []]))
    allOrganizations.forEach((organization) => {
      const providerId = organization.provider_id || (providers.length === 1 ? providers[0]?.id : '')
      if (grouped.has(providerId)) grouped.get(providerId).push(organization)
    })
    return grouped
  }, [allOrganizations, providers, visibleProviders])
  const selectedOrgs = useMemo(() => {
    const normalizedLoadedOrgs = loadedGroupOrgs.map(normalizeCommandCenterOrganization)
    const byId = new Map([...normalizedLoadedOrgs, ...allOrganizations].map((org) => [org.id, org]))
    return selectedOrgIds.map((id) => byId.get(id)).filter(Boolean)
  }, [allOrganizations, loadedGroupOrgs, selectedOrgIds])
  // Derived rather than synced: deselecting the open book (or hydrating an
  // edit) must never leave the tab strip pointing at a book that is gone, and
  // an effect that "fixes" the state afterwards would render one frame of
  // nothing first.
  const activeDestinationTabValue = selectedOrgs.some((org) => org.id === activeDestinationOrgId)
    ? activeDestinationOrgId
    : selectedOrgs[0]?.id || ''
  const marketValidationError = pendingKambiValidationError || authoredWagerValidationError
  const regulationAvailabilityError = kambiRegulationAvailabilityError(
    kambiValidation,
    selectedOrgs,
    formData.targetOfferingsMap,
    formData.kambiOutcomeIds,
  )
  // The destination-scoped V2 shape Kambi validation needs: one entry per
  // selected organization that has at least one selected regulation, plus
  // that organization's own selected regulation IDs. It never carries or
  // implies a source organization. An organization with zero selected
  // regulations is omitted rather than sent with an empty regulation_ids
  // array: the backend rejects the *entire* request the moment any one
  // destination has no regulations (ValidateKambiOutcomeAvailability), so
  // including it would permanently block outcome validation for every
  // other, still-scoped destination. Preflight's own per-destination
  // regulation_required check (post_group_v2_validation.go) is the correct
  // place for that org's empty scope to surface.
  const kambiDestinations = useMemo(() => (
    selectedOrgIds
      .map((organizationId) => ({
        organization_id: organizationId,
        regulation_ids: formData.targetOfferingsMap[organizationId] || EMPTY_ARRAY,
      }))
      .filter((destination) => destination.regulation_ids.length > 0)
  ), [selectedOrgIds, formData.targetOfferingsMap])
  const kambiAllOfferings = useMemo(() => (
    Array.from(new Set(selectedOrgs.flatMap((organization) => organization.kambi_offering_ids || EMPTY_ARRAY)))
  ), [selectedOrgs])

  const creatorsQuery = useQuery({
    queryKey: ['cc-creators', selectedOrgIds.join(','), creatorSearch],
    queryFn: () => commandCenterApi.searchCreators({
      organization_ids: selectedOrgIds.join(','),
      q: creatorSearch,
    }),
    enabled: selectedOrgIds.length > 0,
  })
  const availableCreators = creatorsQuery.data?.creators || EMPTY_ARRAY

  useEffect(() => {
    if (!selectedCreator?.id || availableCreators.length === 0) return
    const hydrated = availableCreators.find((creator) => creator.id === selectedCreator.id)
    if (hydrated && hydrated.display_name !== selectedCreator.display_name) {
      setSelectedCreator(normalizeSelectedCreator(hydrated))
    }
  }, [availableCreators, selectedCreator?.display_name, selectedCreator?.id])

  useEffect(() => {
    if (selectedOrgs.length === 0) {
      setPreviewOrganizationId('')
      return
    }
    if (!selectedOrgs.some((org) => org.id === previewOrganizationId)) {
      setPreviewOrganizationId(selectedOrgs[0].id)
    }
  }, [previewOrganizationId, selectedOrgs])


  const aiCapabilitiesQuery = useQuery(AI_CAPABILITIES_QUERY_OPTIONS)
  const aiAvailability = deriveAiAvailability(aiCapabilitiesQuery)

  const previewOrganization = selectedOrgs.find((organization) => organization.id === previewOrganizationId) || selectedOrgs[0] || null
  const destinationMetadataKey = selectedOrgs.map((organization) => organization.id).join(',')

  useEffect(() => {
    let cancelled = false
    const fetchOrgData = async () => {
      if (selectedOrgs.length === 0) {
        setOrgTags({})
        setOrgSurfaces({})
        setDestinationMetadata({ key: '', loading: false, errors: [], failedTagOrganizationIds: [] })
        return
      }

      setDestinationMetadata({ key: destinationMetadataKey, loading: true, errors: [], failedTagOrganizationIds: [] })
      const results = await Promise.all(selectedOrgs.map(async (org) => {
        const [tagsResult, surfacesResult] = await Promise.allSettled([
          commandCenterApi.listOrgTags(org.slug, true),
          commandCenterApi.listOrgSurfaces(org.slug),
        ])
        const errors = []
        if (tagsResult.status === 'rejected') errors.push(`${org.name} tags`)
        if (surfacesResult.status === 'rejected') errors.push(`${org.name} surfaces`)
        return {
          id: org.id,
          tagsFailed: tagsResult.status === 'rejected',
          tags: tagsResult.status === 'fulfilled' ? tagsResult.value?.tags || [] : [],
          surfaces: surfacesResult.status === 'fulfilled'
            ? (surfacesResult.value?.items || []).filter((surface) => surface.is_enabled)
            : [],
          errors,
        }
      }))

      if (!cancelled) {
        setOrgTags(Object.fromEntries(results.map((result) => [result.id, result.tags])))
        setOrgSurfaces(Object.fromEntries(results.map((result) => [result.id, result.surfaces])))
        setDestinationMetadata({
          key: destinationMetadataKey,
          loading: false,
          errors: results.flatMap((result) => result.errors),
          failedTagOrganizationIds: results.filter((result) => result.tagsFailed).map((result) => result.id),
        })
      }
    }

    fetchOrgData()
    return () => {
      cancelled = true
    }
  }, [destinationMetadataKey, destinationMetadataRetry, selectedOrgs])

  useEffect(() => {
    const availableOrganizationsById = new Map(
      availableOrgs.map((organization) => [organization.id, organization]),
    )
    setFormData((current) => {
      let targetOfferingsMap = pruneTargetMap(
        current.targetOfferingsMap,
        selectedOrgIds,
        (organizationId) => {
          const organization = availableOrganizationsById.get(organizationId)
          return organization ? new Set(organization.kambi_offering_ids) : null
        },
      )
      for (const organizationId of selectedOrgIds) {
        if (Object.hasOwn(targetOfferingsMap, organizationId)) continue
        const organization = availableOrganizationsById.get(organizationId)
        if (!organization) continue
        targetOfferingsMap = {
          ...targetOfferingsMap,
          [organizationId]: [...organization.kambi_offering_ids],
        }
      }
      const tagIdsMap = pruneTargetMap(
        current.tagIdsMap,
        selectedOrgIds,
        (organizationId) => Object.hasOwn(orgTags, organizationId)
          ? new Set((orgTags[organizationId] || []).map((tag) => tag.id))
          : null,
      )
      let surfaceIdsMap = pruneTargetMap(
        current.surfaceIdsMap,
        selectedOrgIds,
        (organizationId) => Object.hasOwn(orgSurfaces, organizationId)
          ? new Set((orgSurfaces[organizationId] || []).map((surface) => surface.id))
          : null,
      )
      for (const organizationId of selectedOrgIds) {
        if (Object.hasOwn(surfaceIdsMap, organizationId) || !Object.hasOwn(orgSurfaces, organizationId)) continue
        const defaultIds = (orgSurfaces[organizationId] || [])
          .filter((surface) => surface.include_by_default)
          .map((surface) => surface.id)
        surfaceIdsMap = { ...surfaceIdsMap, [organizationId]: defaultIds }
      }
      if (
        targetOfferingsMap === current.targetOfferingsMap
        && tagIdsMap === current.tagIdsMap
        && surfaceIdsMap === current.surfaceIdsMap
      ) {
        return current
      }
      return { ...current, targetOfferingsMap, tagIdsMap, surfaceIdsMap }
    })
  }, [availableOrgs, orgSurfaces, orgTags, selectedOrgIds, setFormData])

  const sharedLibraryQuery = useQuery({
    queryKey: ['cc-post-library-assets', assetSearch],
    queryFn: () => commandCenterApi.listMediaAssets({
      ...(assetSearch ? { search: assetSearch } : {}),
    }),
    staleTime: 30_000,
  })
  const sharedLibraryAssets = sharedLibraryQuery.data?.items || EMPTY_ARRAY


  const pollingTranscriptionEntries = useMemo(
    () => Object.entries(transcriptionJobs).filter(([, entry]) => (
      (entry?.status === 'polling' || entry?.status === 'retrying')
      && entry.jobId
    )),
    [transcriptionJobs],
  )
  const transcriptionJobQueries = useQueries({
    queries: pollingTranscriptionEntries.map(([, entry]) => ({
      queryKey: ['cc-ai-transcription-job', entry.orgSlug || '', entry.jobId],
      queryFn: async () => {
        const response = entry.orgSlug
          ? await commandCenterApi.getOrgAiJob(entry.orgSlug, entry.jobId)
          : await commandCenterApi.getAiJob(entry.jobId)
        const status = response?.job?.status
        const stillRunning = !status || status === 'pending' || status === 'processing'
        if (stillRunning && Date.now() - (entry.startedAt || Date.now()) > TRANSCRIPTION_TIMEOUT_MS) {
          const error = new Error('Video analysis timed out')
          error.isTranscriptionTimeout = true
          throw error
        }
        return response
      },
      refetchInterval: (query) => {
        if (query.state.status === 'error') return false
        const status = query.state.data?.job?.status
        return !status || status === 'pending' || status === 'processing' ? 3000 : false
      },
      retry: false,
    })),
  })
  useEffect(() => {
    pollingTranscriptionEntries.forEach(([mediaId], index) => {
      const query = transcriptionJobQueries[index]
      const job = query?.data?.job
      if (job?.status === 'completed') {
        setAiReview((currentValue) => mergeTranscriptionOutput(currentValue, job.result || {}))
        setTranscriptionJobs((currentValue) => {
          const currentEntry = currentValue[mediaId]
          if (!currentEntry || (currentEntry.status !== 'polling' && currentEntry.status !== 'retrying')) return currentValue
          return { ...currentValue, [mediaId]: createVideoAnalysisFromJob(job, currentEntry) }
        })
        return
      }
      if (job?.status === 'failed') {
        setTranscriptionJobs((currentValue) => {
          const currentEntry = currentValue[mediaId]
          if (!currentEntry || (currentEntry.status !== 'polling' && currentEntry.status !== 'retrying')) return currentValue
          return { ...currentValue, [mediaId]: createVideoAnalysisFromJob(job, currentEntry) }
        })
        return
      }
      if (query?.isError) {
        setTranscriptionJobs((currentValue) => {
          const currentEntry = currentValue[mediaId]
          if (!currentEntry || (currentEntry.status !== 'polling' && currentEntry.status !== 'retrying')) return currentValue
          const timeoutError = query.error?.isTranscriptionTimeout
            ? { code: 'analysis_timeout', retryable: true }
            : query.error
          return {
            ...currentValue,
            [mediaId]: { ...currentEntry, ...createVideoAnalysisFromError(timeoutError) },
          }
        })
      }
    })
  }, [pollingTranscriptionEntries, setAiReview, setTranscriptionJobs, transcriptionJobQueries])

  const { data: brandingData } = useQuery({
    queryKey: ['cc-post-branding', previewOrganization?.id],
    queryFn: () => commandCenterApi.getOrgBranding(previewOrganization.id),
    enabled: !!previewOrganization?.id,
  })

  useEffect(() => {
    if (!isEditMode) {
      setDraftReady(true)
      const draft = parseStoredJson(draftKey)
      setPendingDraft(draft)
      return
    }

    let cancelled = false
    const loadGroup = async () => {
      try {
        const group = await commandCenterApi.getPostGroup(groupIdParam)
        if (cancelled) return
        const destinations = group.destinations || []
        const orgs = destinations.map((destination) => ({
          id: destination.organization_id,
          slug: destination.organization_slug,
          name: destination.organization_name,
          provider_id: group.provider_id,
        }))
        const restored = createComposerStateFromGroup(group)
        setLoadedGroupOrgs(orgs)
        setLoadedGroupBaseline(group)
        setSelectedProviderId(group.provider_id || '')
        setSelectedOrgIds(destinations.map((destination) => destination.organization_id))
        setPreviewOrganizationId(destinations[0]?.organization_id || '')
        setSelectedCreator(normalizeSelectedCreator(group.creator))
        setFormData(restored)
        setUploadedMedia(createUploadedMediaFromStoredMedia(restored.mediaObjects))
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to load distributed post')
      } finally {
        if (!cancelled) {
          setLoading(false)
          setDraftReady(true)
        }
      }
    }
    loadGroup()
    return () => {
      cancelled = true
    }
  }, [draftKey, groupIdParam, isEditMode, setFormData])


  const previewOrganizationTags = orgTags[previewOrganization?.id] || EMPTY_ARRAY
  const previewLegs = useMemo(() => {
    if (isPredictionContractProvider && providerSelectionDetails.length > 0) {
      return providerSelectionDetails.map((selection) => ({
        event: {
          name: selection.event_name || '',
          start_time: selection.event_start,
        },
        market: {
          name: selection.market_name || selection.selection_metadata?.market_title || '',
          type: selection.market_type || selection.selection_metadata?.market_type || '',
        },
        selection: {
          name: selection.name || '',
          outcome_id: selection.external_id,
          provider_selection_id: selection.external_id,
          provider_payload: {
            provider: isOG ? 'og' : 'cryptodotcom',
            contract_id: selection.external_id,
            ...(selection.selection_metadata || {}),
          },
        },
        provider_payload: {
          provider: isOG ? 'og' : 'cryptodotcom',
          contract_id: selection.external_id,
          ...(selection.selection_metadata || {}),
        },
        odds: selection.odds_american
          ? { american: selection.odds_american }
          : selection.odds_decimal
            ? { decimal: selection.odds_decimal }
            : null,
      }))
    }
    if (!kambiValidation?.outcomes || !formData.kambiOutcomeIds.length) return []
    const eventsByName = {}
    for (const evt of (kambiValidation.events || [])) {
      eventsByName[evt.name] = evt
    }
    return formData.kambiOutcomeIds
      .map((id) => {
        const data = kambiValidation.outcomes[String(id)]
        if (!data) return null
        const evt = eventsByName[data.event_name]
        return kambiOutcomePreviewLeg(data, evt)
      })
      .filter(Boolean)
  }, [isPredictionContractProvider, isOG, providerSelectionDetails, kambiValidation, formData.kambiOutcomeIds])
  const previewLegPresentations = useMemo(
    () => getPreviewSelections({ id: 'command-center-preview', legs: previewLegs }).map(selectionPresentation),
    [previewLegs],
  )

  const mutationPayload = useMemo(() => buildCommandCenterPostGroupPayloadV2(formData, {
    selectedProviderId,
    selectedOrganizationIds: selectedOrgIds,
    selectedCreator,
    isKambi,
    isCryptoDotCom,
    isOG,
    isEditMode,
    clientRequestId,
    expectedRevision: isEditMode ? loadedGroupBaseline?.revision : undefined,
  }), [
    clientRequestId,
    formData,
    isCryptoDotCom,
    isEditMode,
    isKambi,
    isOG,
    loadedGroupBaseline?.revision,
    selectedCreator,
    selectedOrgIds,
    selectedProviderId,
  ])
  const savedEditDocument = useMemo(() => {
    if (!loadedGroupBaseline) return null
    return buildCommandCenterPostGroupPayloadV2(
      normalizeCommandCenterComposerState(createComposerStateFromGroup(loadedGroupBaseline)),
      {
        selectedProviderId: loadedGroupBaseline.provider_id,
        selectedOrganizationIds: (loadedGroupBaseline.destinations || []).map((destination) => destination.organization_id),
        selectedCreator: normalizeSelectedCreator(loadedGroupBaseline.creator),
        isEditMode: true,
        isKambi,
        isCryptoDotCom,
        isOG,
      },
    ).document
  }, [isCryptoDotCom, isKambi, isOG, loadedGroupBaseline])
  const hasUnsavedEdits = isEditMode && !loading && savedEditDocument
    && JSON.stringify(mutationPayload.document) !== JSON.stringify(savedEditDocument)
  // Approval routing does not change destination capability. Keep a completed
  // preflight current when the operator makes that final posting decision.
  const preflightSignature = useMemo(() => JSON.stringify({
    ...mutationPayload,
    document: {
      ...mutationPayload.document,
      shared: {
        ...mutationPayload.document.shared,
        advanced: {
          ...mutationPayload.document.shared.advanced,
          skip_approval: false,
        },
      },
    },
  }), [mutationPayload])
  const effectivePreviewDocument = useMemo(
    () => resolveCommandCenterDestinationDocumentV2(mutationPayload, previewOrganization?.id),
    [mutationPayload, previewOrganization?.id],
  )
  const previewPayload = useMemo(() => {
    const previewTags = orgTags[previewOrganization?.id] || []
    const selectedTagObjects = previewTags.filter((tag) => (
      effectivePreviewDocument.distribution?.tag_ids || []
    ).includes(tag.id))
    const advanced = effectivePreviewDocument.advanced || {}
    return {
      payload: {
        post_id: 'preview',
        text: effectivePreviewDocument.text || '',
        title: effectivePreviewDocument.title || '',
        subtitle: effectivePreviewDocument.subtitle || '',
        user: selectedCreator
          ? { display_name: selectedCreator.display_name, avatar_url: selectedCreator.avatar_url }
          : { display_name: 'Creator Name' },
        media_objects: (effectivePreviewDocument.media_objects || []).map((media) => ({
          type: media.type,
          url: media.url,
        })),
        legs: previewLegs,
        selection_set: isKambi
          ? buildAuthoredWagerPreviewSelectionSet(formData.wagerEntries, previewLegs)
          : undefined,
        cta: effectivePreviewDocument.cta,
        tags: selectedTagObjects.map((tag) => ({
          label: tag.label,
          bg_color: tag.bg_color,
          text_color: tag.text_color,
          icon: tag.icon,
          font_style: tag.font_style,
          position: tag.position,
        })),
        post_type: advanced.post_type,
        expires_at: advanced.expires_at,
        promo_priority: advanced.promo_priority,
        interleave_every: advanced.interleave_every,
        created_at: new Date().toISOString(),
        share_enabled: advanced.share_enabled !== false,
        copy_count: advanced.copy_count_enabled ? 23 : undefined,
        card_presentation: advanced.card_presentation,
      },
    }
  }, [effectivePreviewDocument, formData.wagerEntries, isKambi, orgTags, previewLegs, previewOrganization?.id, selectedCreator])

  const setField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }))
  }

  const handleKambiValidationResult = useCallback((result) => {
    setKambiValidation(result)
    if (!result?.outcomes) return
    setFormData((current) => {
      const wagerEntries = applyValidatedDefaultWagerType(current.wagerEntries, result.outcomes)
      return wagerEntries === current.wagerEntries ? current : { ...current, wagerEntries }
    })
  }, [setFormData])

  const handleProviderSelectionIdsChange = (ids, selections = providerSelectionDetails) => {
    setProviderSelectionDetails(selections)
    setFormData((current) => {
      const next = { ...current, providerSelectionIds: ids }
      if (isPredictionContractProvider && (current.ctaKind === 'betslip' || current.ctaKind === 'provider-cart')) {
        if (ids.length === 0) {
          return {
            ...next,
            ctaKind: 'betslip',
            ctaLabel: '',
            ctaTarget: '',
          }
        }
        const first = selections[0]
        const meta = first?.selection_metadata || {}
        next.ctaKind = 'provider-cart'
        next.ctaLabel = ids.length === 1
          ? `${isOG ? 'Trade' : 'Buy'} ${providerContractLabel(first, isOG)}`
          : ids.length > 1
            ? 'Trade'
            : current.ctaLabel
        next.ctaTarget = meta.detail_url || current.ctaTarget || ''
      }
      return next
    })
  }

  const setMapValue = (field, orgId, value) => {
    setFormData((current) => ({
      ...current,
      [field]: {
        ...current[field],
        [orgId]: value,
      },
    }))
  }
  const setKambiOutcomeIds = (update) => setFormData((current) => {
    const ids = typeof update === 'function' ? update(current.kambiOutcomeIds) : update
    return {
      ...current,
      kambiOutcomeIds: ids,
      wagerEntries: reconcileAuthoredWagerEntries(current.wagerEntries, ids, { preferParlay: true }),
    }
  })
  const addKambiOutcomeId = (outcomeId) => {
    setKambiOutcomeIds((ids) => (ids.includes(outcomeId) ? ids : [...ids, outcomeId]))
  }
  const handleSelectKambiSearchResult = (result) => {
    const outcomeId = parseOutcomeId(result?.leg?.outcome_id)
    if (outcomeId === null) {
      toast.error('This search result cannot be passed safely to provider validation. Choose another market.')
      return
    }
    addKambiOutcomeId(outcomeId)
  }
  const resetProviderSelection = (providerId, organizationIds = []) => {
    setSelectedProviderId(providerId)
    setSelectedOrgIds(organizationIds)
    setSelectedCreator(null)
    setMarketSearchQuery('')
    setPreviewOrganizationId(organizationIds[0] || '')
    setFormData((current) => ({
      ...current,
      kambiOutcomeIds: [],
      wagerEntries: [],
      providerSelectionIds: [],
      tagIdsMap: {},
      targetOfferingsMap: {},
      surfaceIdsMap: {},
      bookOverrides: {},
    }))
    setProviderSelectionDetails([])
    setPreflightState(null)
  }

  const toggleProviderOrganizations = (providerId) => {
    if (selectedProviderId && selectedProviderId !== providerId) {
      toast.info('A distributed post can target one provider. Shared content was retained; provider-specific choices were reset.')
    }
    const organizationIds = (organizationsByProvider.get(providerId) || []).map((organization) => organization.id)
    const allSelected = selectedProviderId === providerId
      && organizationIds.length > 0
      && organizationIds.every((organizationId) => selectedOrgIds.includes(organizationId))
    resetProviderSelection(providerId, allSelected ? [] : organizationIds)
  }

  const selectOrganization = (providerId, organizationId) => {
    if (selectedProviderId && selectedProviderId !== providerId) {
      toast.info('A distributed post can target one provider. Shared content was retained; provider-specific choices were reset.')
    }
    if (selectedProviderId !== providerId) {
      resetProviderSelection(providerId, [organizationId])
      return
    }
    handleOrgToggle(organizationId)
  }

  const setBookOverride = (organizationId, field, value) => {
    setFormData((current) => ({
      ...current,
      bookOverrides: {
        ...current.bookOverrides,
        [organizationId]: {
          ...(current.bookOverrides?.[organizationId] || {}),
          [field]: { value },
        },
      },
    }))
  }

  const clearBookOverride = (organizationId, field) => {
    setFormData((current) => {
      const overrides = { ...(current.bookOverrides?.[organizationId] || {}) }
      delete overrides[field]
      return {
        ...current,
        bookOverrides: {
          ...current.bookOverrides,
          [organizationId]: overrides,
        },
      }
    })
  }

  const toggleBookMediaAsset = (organizationId, asset) => {
    const current = formData.bookOverrides?.[organizationId]?.media_objects?.value || []
    const existing = current.find((media) => (media.mediaId || media.asset_id || media.id) === asset.id)
    const value = existing
      ? current.filter((media) => (media.mediaId || media.asset_id || media.id) !== asset.id)
      : [...current, {
        id: crypto.randomUUID(),
        mediaObjectId: crypto.randomUUID(),
        mediaId: asset.id,
        type: asset.asset_type || asset.type,
        url: asset.url,
        object_key: asset.object_key,
        source_object_key: asset.source_object_key,
      }]
    setBookOverride(organizationId, 'media_objects', value)
  }

  const handleOrgToggle = (orgId) => {
    const removing = selectedOrgIds.includes(orgId)
    setSelectedOrgIds((current) => (
      removing ? current.filter((id) => id !== orgId) : [...current, orgId]
    ))
    if (removing) {
      setFormData((current) => {
        const removeOrganization = (targetMap) => Object.fromEntries(
          Object.entries(targetMap || {}).filter(([organizationId]) => organizationId !== orgId),
        )
        return {
          ...current,
          targetOfferingsMap: removeOrganization(current.targetOfferingsMap),
          tagIdsMap: removeOrganization(current.tagIdsMap),
          surfaceIdsMap: removeOrganization(current.surfaceIdsMap),
          bookOverrides: removeOrganization(current.bookOverrides),
        }
      })
    }
    setPreflightState(null)
  }

  const toggleOffering = (orgId, offeringId) => {
    const current = formData.targetOfferingsMap[orgId] || []
    setMapValue(
      'targetOfferingsMap',
      orgId,
      current.includes(offeringId)
        ? current.filter((id) => id !== offeringId)
        : [...current, offeringId]
    )
  }

  // "Where it runs" gains the Select all / Clear affordances the single-book
  // editor has always had; the chip picker it replaces had neither.
  const setOfferingsForOrganization = (orgId, offeringIds) => {
    setMapValue('targetOfferingsMap', orgId, offeringIds)
  }

  const toggleTag = (orgId, tagId) => {
    const current = formData.tagIdsMap[orgId] || []
    setMapValue(
      'tagIdsMap',
      orgId,
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId]
    )
  }

  const toggleSurface = (orgId, surfaceId) => {
    const surfaces = orgSurfaces[orgId] || []
    const defaultIds = surfaces.filter((surface) => surface.include_by_default).map((surface) => surface.id)
    const current = Array.isArray(formData.surfaceIdsMap[orgId])
      ? formData.surfaceIdsMap[orgId]
      : defaultIds
    setMapValue(
      'surfaceIdsMap',
      orgId,
      current.includes(surfaceId)
        ? current.filter((id) => id !== surfaceId)
        : [...current, surfaceId]
    )
  }

  // Command Center uploads and generated assets are shared. Imported assets
  // retain the organization that owns their library.
  const attachAssetToComposer = (asset) =>
    attachAssetToState(asset, {
      setUploadedMedia,
      setFormData,
    })

  const queueVideoAnalysis = async ({ mediaId, mediaUrl, title, contentType }) => {
    const entry = {
      mediaUrl,
      title,
      contentType,
      startedAt: Date.now(),
    }
    let capabilities = null

    try {
      capabilities = await queryClient.ensureQueryData(AI_CAPABILITIES_QUERY_OPTIONS)
    } catch (error) {
      setTranscriptionJobs((currentValue) => ({
        ...currentValue,
        [mediaId]: {
          ...entry,
          ...createUnavailableVideoAnalysis({ code: 'capability_unavailable' }),
          error: aiUnavailableMessage(deriveAiAvailability({ isError: true, error }), 'transcription'),
        },
      }))
      return
    }

    if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return

    const transcriptionCapability = capabilities?.features?.transcription
    if (transcriptionCapability?.enabled !== true) {
      setTranscriptionJobs((currentValue) => ({
        ...currentValue,
        [mediaId]: {
          ...entry,
          ...createUnavailableVideoAnalysis(transcriptionCapability?.reason),
          error: aiUnavailableMessage(deriveAiAvailability({ data: capabilities }), 'transcription'),
        },
      }))
      return
    }

    try {
      const response = await commandCenterApi.createTranscriptionJob({
        media_url: mediaUrl,
        title,
        content_type: contentType,
        context_type: 'post_group_draft',
      })
      const job = response?.job
      if (!job?.id) throw new Error('Video analysis job was not created')
      if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return

      setTranscriptionJobs((currentValue) => ({
        ...currentValue,
        [mediaId]: createVideoAnalysisFromJob(job, entry),
      }))
      if (job.status === 'completed') {
        setAiReview((currentValue) => mergeTranscriptionOutput(currentValue, job.result || {}))
      }
    } catch (error) {
      if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return
      setTranscriptionJobs((currentValue) => ({
        ...currentValue,
        [mediaId]: { ...entry, ...createVideoAnalysisFromError(error) },
      }))
    }
  }

  const handleMediaUpload = (files) => {
    mediaUploads.enqueue(files, {
      discard: (asset) => { commandCenterApi.deleteMediaAsset(asset.id).catch(() => {}) },
      upload: (file, mediaType, options) => commandCenterApi.uploadMediaAsset(file, { title: file.name, asset_type: mediaType, content_type: mediaUploadContentType(file) }, options),
      attach: (id, mediaType, file, asset) => {
        removedUploadPlaceholderIdsRef.current.delete(id)
        setUploadedMedia((current) => current.map((media) => media.id === id ? {
          ...media,
          mediaObjectId: id,
          mediaId: asset.id,
          objectKey: asset.object_key || null,
          url: asset.url,
        } : media))
        setFormData((current) => ({
          ...current,
          mediaObjects: current.mediaObjects.some((media) => media.id === id) ? current.mediaObjects : [...current.mediaObjects, {
            id,
            mediaObjectId: id,
            mediaId: asset.id,
            object_key: asset.object_key || null,
            type: mediaType,
            url: asset.url,
            name: file.name,
          }],
        }))
      },
      analyze: async (id, file, asset) => {
        await queueVideoAnalysis({ mediaId: id, mediaUrl: asset.url, title: file.name, contentType: mediaUploadContentType(file) })
      },
    })
  }

  const handleRetryTranscription = async (mediaId) => {
    const entry = transcriptionJobs[mediaId]
    if (
      !isRetryableVideoAnalysis(entry)
      || retryingTranscriptionMediaIdsRef.current.has(mediaId)
    ) return

    retryingTranscriptionMediaIdsRef.current.add(mediaId)
    setTranscriptionJobs((currentValue) => {
      const currentEntry = currentValue[mediaId]
      if (!isRetryableVideoAnalysis(currentEntry)) return currentValue
      return { ...currentValue, [mediaId]: { ...currentEntry, status: 'retrying', error: '' } }
    })

    try {
      const response = entry.orgSlug
        ? await commandCenterApi.retryOrgAiJob(entry.orgSlug, entry.jobId)
        : await commandCenterApi.retryAiJob(entry.jobId)
      const job = response?.job
      if (!job?.id) throw new Error('Video analysis retry did not return a job ID')
      if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return

      setTranscriptionJobs((currentValue) => {
        if (!currentValue[mediaId]) return currentValue
        return {
          ...currentValue,
          [mediaId]: createVideoAnalysisFromJob(job, {
            ...entry,
            startedAt: Date.now(),
          }),
        }
      })
      if (job.status === 'completed') {
        setAiReview((currentValue) => mergeTranscriptionOutput(currentValue, job.result || {}))
      }
    } catch (error) {
      setTranscriptionJobs((currentValue) => {
        const currentEntry = currentValue[mediaId]
        if (!currentEntry) return currentValue
        return {
          ...currentValue,
          [mediaId]: { ...currentEntry, ...createVideoAnalysisFromError(error) },
        }
      })
    } finally {
      retryingTranscriptionMediaIdsRef.current.delete(mediaId)
    }
  }

  const removeMedia = (index) => {
    const media = uploadedMedia[index]
    if (!media) return
    mediaUploads.remove(media.id)
    removedUploadPlaceholderIdsRef.current.add(media.id)
    const removedJob = transcriptionJobs[media.id]
    const hasOtherCompletedAnalysis = uploadedMedia.some((item, itemIndex) => (
      itemIndex !== index && transcriptionJobs[item.id]?.status === 'completed'
    ))
    removeMediaAtIndex(index, { uploadedMedia, setUploadedMedia, setFormData })
    setTranscriptionJobs((currentValue) => {
      if (!currentValue[media.id]) return currentValue
      const nextValue = { ...currentValue }
      delete nextValue[media.id]
      return nextValue
    })
    if (removedJob?.status === 'completed' && !hasOtherCompletedAnalysis) {
      setAiReview(createComposerAiReview())
    }
  }

  const suggestCaptionMutation = useMutation({
    mutationFn: (directionOverride) => commandCenterApi.suggestCaption({
      text: formData.text || '',
      direction: String(directionOverride || captionGuidance).trim() || undefined,
      count: 3,
    }),
    onSuccess: (response) => {
      const parsed = parseAiJson(response.response)
      const captions = [
        ...(Array.isArray(response?.captions) ? response.captions : []),
        ...(Array.isArray(parsed?.captions) ? parsed.captions : []),
        parsed?.caption,
        response?.caption,
      ].filter((caption, index, values) => caption && values.indexOf(caption) === index)
      setAiReview((current) => ({ ...current, caption: captions[0] || '', captions: captions.slice(0, 3) }))
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to suggest caption'),
  })

  const suggestTagsMutation = useMutation({
    mutationFn: () => commandCenterApi.suggestTags(formData.text || ''),
    onSuccess: (response) => {
      const parsed = parseAiJson(response.response)
      setAiReview((current) => ({ ...current, tags: parsed?.tags || [] }))
      setTagSuggestionsRevision((value) => value + 1)
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to suggest tags'),
  })

  const extractBetDetailsMutation = useMutation({
    mutationFn: () => commandCenterApi.extractBetDetails(formData.text || ''),
    onSuccess: (response) => {
      const parsed = parseAiJson(response.response)
      setAiReview((current) => ({ ...current, betSummary: parsed?.summary || null }))
    },
    onError: (error) => toast.error(error.response?.data?.error || (isPredictionContractProvider ? 'Failed to extract contract details' : 'Failed to extract bet details')),
  })

  const referenceImageState = useReferenceImages({
    orgSlug: COMMAND_CENTER_REFERENCE_SCOPE,
    value: referenceImages,
    onChange: setReferenceImages,
    uploadFile: commandCenterApi.uploadMediaAsset,
    deleteAsset: commandCenterApi.deleteMediaAsset,
  })

  const generateImageMutation = useImageGenerationJob({
    scope: draftKey,
    job: imageGenerationJob,
    setJob: setImageGenerationJob,
    buildInput: (size = DEFAULT_AI_IMAGE_SIZE) => {
      const prompt = imagePrompt.trim()
      if (!prompt) throw new Error('Add prompt direction before generating an image')
      return {
        prompt,
        size,
        referenceAssetIds: referenceImageState.items.filter((reference) => reference.status === 'ready').map((reference) => reference.id),
      }
    },
    createJob: ({ prompt, size, referenceAssetIds }) => commandCenterApi.createImageJob({
      prompt,
      size,
      context_type: 'post_group_draft',
      ...(referenceAssetIds.length ? { reference_asset_ids: referenceAssetIds } : {}),
    }),
    getJob: (id) => commandCenterApi.getAiJob(id),
    complete: (job) => {
      if (!job.result?.url) throw new Error('Image generation returned no asset URL')
      if (!job.result?.asset_id) throw new Error('Image generation returned no library asset')
      return job
    },
    onSuccess: (job, { prompt, referenceAssetIds }) => {
      // The job has finished. Delete only its references; later edits stay.
      if (referenceAssetIds.length) {
        const sent = new Set(referenceAssetIds)
        setReferenceImages((current) => (current || []).filter((reference) => !sent.has(reference?.id)))
        referenceAssetIds.forEach((id) => {
          Promise.resolve()
            .then(() => commandCenterApi.deleteMediaAsset(id))
            .catch(() => {})
        })
      }
      const generatedAssetId = job.result.asset_id
      attachAssetToComposer({
        id: generatedAssetId,
        library_scope: 'command_center',
        object_key: job.result.object_key,
        asset_type: 'image',
        title: prompt.slice(0, 60) || 'Generated image',
        url: job.result.url,
      })
      setGeneratedAssetIds((current) => Array.from(new Set([...(current || []), generatedAssetId])))
      setImagePrompt((current) => current.trim() === prompt ? '' : current)
    },
    onError: (error) => toast.error(imageGenerationErrorMessage(error)),
  })

  const postTextLength = formData.text.trim().length
  // Every AI entry point uses the same capabilities and feature reasons.
  const aiTextUnavailableReason = aiUnavailableMessage(aiAvailability, 'text_suggestions')
  const imageGenerationCapabilities = imageGenerationCapabilitiesFor(aiAvailability)
  const captionSuggestionReason = aiTextUnavailableReason || (
    postTextLength >= 12 ? '' : 'Add at least 12 characters of post text before suggesting a caption.'
  )
  const tagsSuggestionReason = aiTextUnavailableReason || (
    postTextLength >= 12 ? '' : 'Add at least 12 characters of post text before suggesting tags.'
  )
  const betDetailsReason = aiTextUnavailableReason || (
    postTextLength >= 12 ? '' : `Add pasted ${isPredictionContractProvider ? 'contract' : 'bet'} details or post copy before extracting ${isPredictionContractProvider ? 'contract' : 'bet'} details.`
  )

  const runTextAiAction = (reason, mutation, variables) => {
    if (reason) {
      toast.error(reason)
      return
    }
    mutation.mutate(variables)
  }

  const handleApplyCaption = () => {
    setField('text', aiReview.caption)
  }

  const handleExternalCaptionApply = (caption) => {
    if (!caption) return
    setField('text', caption)
  }

  const tagSuggestions = Array.isArray(aiReview.tags) ? aiReview.tags : EMPTY_ARRAY
  const showTagSuggestionChips = tagSuggestions.length > 0
  // Opens "More content options", where the chips live, only when the
  // operator's own Suggest tags request returns, even after they folded it.
  // Tags from video analysis or a restored draft only label the folded
  // section (moreOptionsNote) instead of opening it mid-upload.
  const tagSuggestionsRevealKey = tagSuggestionsRevision
  const tagSuggestionsNote = showTagSuggestionChips
    ? `${tagSuggestions.length} suggested ${tagSuggestions.length === 1 ? 'tag' : 'tags'} to review`
    : ''

  // Each book matches the suggestions against its own saved tags and gets its
  // own tagIdsMap entry; a book with no match is left as it was. The toast
  // names only the books that actually gained a tag. A book whose tag read
  // failed has no catalog to match, so it is left out (the destination
  // settings error already asks for a retry); the action itself waits while
  // the catalogs load (see tagSuggestionChips).
  const applySuggestedTagsToEveryBook = () => {
    const failedTagOrganizationIds = new Set(destinationMetadata.failedTagOrganizationIds || EMPTY_ARRAY)
    const readableOrganizations = selectedOrgs.filter((organization) => !failedTagOrganizationIds.has(organization.id))
    if (readableOrganizations.length === 0) {
      toast.error(`Saved tags could not load for ${selectedOrgs.map((organization) => organization.name).join(', ')}.`)
      return
    }
    const matches = readableOrganizations.map((organization) => {
      const current = formData.tagIdsMap[organization.id] || EMPTY_ARRAY
      const ids = matchSuggestedTagIds(orgTags[organization.id] || EMPTY_ARRAY, tagSuggestions)
      return { organization, ids, newIds: ids.filter((id) => !current.includes(id)) }
    })
    if (matches.every(({ ids }) => ids.length === 0)) {
      toast.error('No saved tag in the selected books matches these suggestions')
      return
    }
    const additions = matches.filter(({ newIds }) => newIds.length > 0)
    if (additions.length === 0) {
      toast.info('Matching tags are already selected')
      return
    }
    setFormData((current) => {
      const tagIdsMap = { ...current.tagIdsMap }
      additions.forEach(({ organization, newIds }) => {
        tagIdsMap[organization.id] = Array.from(new Set([...(tagIdsMap[organization.id] || []), ...newIds]))
      })
      return { ...current, tagIdsMap }
    })
    toast.success(`Matching tags added for ${additions.map(({ organization }) => organization.name).join(', ')}`)
  }

  const restoreDraft = () => {
    if (!pendingDraft) return
    const draftAdjunct = pendingDraft.adjunct || pendingDraft
    setSelectedProviderId(pendingDraft.selectedProviderId || '')
    setClientRequestId(pendingDraft.clientRequestId || crypto.randomUUID())
    setPreviewOrganizationId(pendingDraft.previewOrganizationId || pendingDraft.selectedOrgIds?.[0] || '')
    setSelectedOrgIds(pendingDraft.selectedOrgIds || [])
    setSelectedCreator(normalizeSelectedCreator(pendingDraft.selectedCreator))
    setFormData(normalizeCommandCenterComposerState(pendingDraft.formData || {}))
    const restoredMedia = createUploadedMediaFromStoredMedia(pendingDraft.uploadedMedia || [])
    setUploadedMedia(restoredMedia)
    setAiReview(normalizeComposerAiReview(draftAdjunct.aiReview))
    setTranscriptionJobs(normalizeDraftTranscriptionJobs(draftAdjunct.transcriptionJobs, restoredMedia))
    setImagePrompt(typeof draftAdjunct.imagePrompt === 'string' ? draftAdjunct.imagePrompt : '')
    const restoredReferences = normalizeComposerReferenceImages(draftAdjunct.referenceImages)
    const usableReferences = restoredReferences.filter((reference) => reference.orgSlug === COMMAND_CENTER_REFERENCE_SCOPE)
    setReferenceImages(usableReferences)
    if (usableReferences.length < restoredReferences.length) toast.info('Upload this draft\'s reference images again.')
    setGeneratedAssetIds(Array.isArray(draftAdjunct.generatedAssetIds) ? draftAdjunct.generatedAssetIds : [])
    if (draftAdjunct.imageGenerationJob) {
      setImageGenerationJob(restoreImageGenerationJob(draftAdjunct.imageGenerationJob))
    }
    setPendingDraft(null)
    toast.success('Draft restored')
  }

  const discardDraft = () => {
    removeStoredValue(draftKey)
    setPendingDraft(null)
  }

  const hasInProgressContent = useMemo(
    () => hasComposerContent(formData, uploadedMedia, selectedProviderId, selectedOrgIds, selectedCreator, adjunct),
    [adjunct, formData, selectedCreator, selectedOrgIds, selectedProviderId, uploadedMedia]
  )

  useEffect(() => {
    if (!draftReady || isEditMode || pendingDraft || isSubmitting) return
    if (!hasInProgressContent) {
      removeStoredValue(draftKey)
      return
    }
    persistStoredJson(draftKey, {
      version: 2,
      savedAt: new Date().toISOString(),
      selectedProviderId,
      clientRequestId,
      previewOrganizationId,
      selectedOrgIds,
      selectedCreator: normalizeSelectedCreator(selectedCreator),
      formData,
      uploadedMedia: uploadedMedia.map((media) => ({
        id: media.id,
        mediaObjectId: media.mediaObjectId,
        type: media.type,
        name: media.name,
        uploadPhase: media.uploadPhase,
        uploadSize: media.uploadSize,
        mediaId: media.mediaId,
        objectKey: media.objectKey,
        sourceObjectKey: media.sourceObjectKey,
        url: media.url,
        previewUrl: media.url || (String(media.previewUrl || '').startsWith('blob:') ? null : media.previewUrl),
        // Draft-only operation context: which library holds this asset.
        organizationId: media.organizationId || null,
        organizationName: media.organizationName || null,
        organizationSlug: media.organizationSlug || null,
      })),
      adjunct: {
        aiReview,
        transcriptionJobs: Object.entries(transcriptionJobs)
          .filter(([mediaId]) => uploadedMedia.some((media) => media.id === mediaId))
          .map(([mediaId, entry]) => ({
            mediaId,
            jobId: entry.jobId || null,
            ...(entry.orgSlug ? { orgSlug: entry.orgSlug } : {}),
            status: entry.status,
            error: entry.error || '',
            errorCode: entry.errorCode || null,
            retryable: entry.retryable === true,
            attempt: entry.attempt || 1,
            retryOf: entry.retryOf || null,
            mediaUrl: entry.mediaUrl || null,
            title: entry.title || null,
            contentType: entry.contentType || null,
            startedAt: entry.startedAt,
            completedAt: entry.completedAt || null,
          })),
        imagePrompt,
        referenceImages,
        imageGenerationJob,
        generatedAssetIds,
      },
    })

  }, [
    aiReview,
    clientRequestId,
    draftKey,
    draftReady,
    formData,
    generatedAssetIds,
    hasInProgressContent,
    imageGenerationJob,
    imagePrompt,
    isEditMode,
    isSubmitting,
    pendingDraft,
    previewOrganizationId,
    referenceImages,
    selectedCreator,
    selectedOrgIds,
    selectedProviderId,
    transcriptionJobs,
    uploadedMedia,
  ])

  const changeComposerMode = (nextMode) => {
    setComposerMode(nextMode)
    persistStoredJson(PREFERENCES_STORAGE_KEY, { composerMode: nextMode })
    setStepError(null)
  }

  const hasClientMediaWork = uploadedMedia.some((media) => media.uploading || media.captionUploading)
  useClientWorkGuard(
    hasClientMediaWork || Boolean(hasUnsavedEdits && !isSubmitting),
    hasClientMediaWork ? undefined : 'You have unsaved changes to this distributed post. Leaving will discard them. Leave this page?',
  )

  useBlobPreviewUrlCleanup(uploadedMedia)

  const hasPendingMediaUploads = hasUnfinishedMediaUploads(uploadedMedia)
  const unfinishedMediaMessage = uploadedMedia.some((media) => media.uploading)
    ? 'Wait for media uploads to finish before continuing.'
    : 'Retry, replace, or remove unfinished files before continuing.'
  // Deselecting a destination never deletes its media and never silently
  // re-attributes it. Attachments whose owning library is no longer a selected
  // destination block review until the operator re-selects the owner or removes
  // the media.
  const orphanedMediaOwners = useMemo(
    () => findOrphanedMediaOwners(uploadedMedia, selectedOrgIds),
    [selectedOrgIds, uploadedMedia],
  )
  const mediaOwnershipError = orphanedMediaOwners.length > 0
    ? describeOrphanedMediaOwners(orphanedMediaOwners)
    : ''
  const preflightMatchesDocument = Boolean(
    preflightState?.signature === preflightSignature
    && preflightState.result,
  )
  const preflightIsCurrent = preflightMatchesDocument && Boolean(preflightState.result.valid)
  const destinationMetadataPending = selectedOrgIds.length > 0 && (
    destinationMetadata.key !== destinationMetadataKey
    || destinationMetadata.loading
  )
  const requiredMetadataFailed = Boolean(
    providersQuery.isError
    || organizationsQuery.isError
    || creatorsQuery.isError
    || destinationMetadata.errors.length > 0
  )
  const requiredMetadataPending = Boolean(
    providersQuery.isPending
    || organizationsQuery.isPending
    || (selectedOrgIds.length > 0 && creatorsQuery.isPending)
    || destinationMetadataPending
  )
  const canReview = Boolean(
    selectedProviderId
    && selectedOrgIds.length > 0
    && selectedCreator?.id
    && formData.text.trim()
    && !hasPendingMediaUploads
    && !requiredMetadataFailed
    && !requiredMetadataPending
  )
  // Deliberately NOT part of `canReview`: a dead Review button would hide the
  // reason. Review stays clickable and `runPreflight` reports the ownership
  // problem, while the stale-signature check below still prevents submission.
  const canSubmit = canReview && preflightIsCurrent && !mediaOwnershipError

  const currentStepIndex = COMMAND_CENTER_COMPOSER_STEPS.findIndex((step) => step.value === activeStep)
  const showStepError = (error) => {
    setStepError(error)
    if (error?.step) setActiveStep(error.step)
    window.requestAnimationFrame(() => errorSummaryRef.current?.focus())
  }
  const runPreflight = async () => {
    if (requiredMetadataPending || requiredMetadataFailed) {
      showStepError({
        step: selectedOrgIds.length > 0 ? 'distribution' : 'content',
        message: requiredMetadataPending
          ? 'Wait for required book metadata to finish loading.'
          : 'Required book metadata could not load. Retry before reviewing destinations.',
      })
      return false
    }
    if (hasPendingMediaUploads) {
      showStepError({ step: 'content', message: unfinishedMediaMessage })
      return false
    }
    if (mediaOwnershipError) {
      showStepError({ step: 'content', message: mediaOwnershipError })
      return false
    }
    const validationError = validateCommandCenterComposerStep('review', formData, {
      selectedProviderId,
      selectedOrganizationIds: selectedOrgIds,
      selectedCreator,
      marketValidationError,
      reviewValidationError: regulationAvailabilityError,
    })
    if (validationError) {
      showStepError(validationError)
      return false
    }

    setIsPreflighting(true)
    try {
      const result = await commandCenterApi.preflightPostGroup(mutationPayload, isEditMode ? groupIdParam : undefined)
      setPreflightState({ signature: preflightSignature, result })
      if (!result?.valid) {
        const globalMessages = (result?.conflicts || []).map((conflict) => conflict.message)
        const destinationMessages = (result?.destinations || []).flatMap((destination) => (
          (destination.conflicts || []).map((conflict) => (
            destination.organization_name && conflict.message
              ? `${destination.organization_name}: ${conflict.message}`
              : conflict.message
          ))
        ))
        const messages = [...new Set([...globalMessages, ...destinationMessages].filter(Boolean))]
        showStepError({
          step: 'review',
          message: messages.length > 0
            ? messages.join(' ')
            : 'One or more destinations cannot accept this post.',
        })
        return false
      }
      setStepError(null)
      setReviewExpanded(true)
      if (composerMode === 'one-page') {
        window.requestAnimationFrame(() => (
          document.getElementById('post-composer-section-review')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
        ))
      }
      return true
    } catch (error) {
      showStepError({ step: 'review', message: extractErrorMessage(error) })
      return false
    } finally {
      setIsPreflighting(false)
    }
  }
  const continueToNextStep = async () => {
    if (activeStep === 'content' && hasPendingMediaUploads) {
      showStepError({ step: 'content', message: unfinishedMediaMessage })
      return
    }
    // Orphaned media is a content-step problem wherever the operator noticed it,
    // so send them back to the media workflow instead of advancing.
    if (mediaOwnershipError) {
      showStepError({ step: 'content', message: mediaOwnershipError })
      return
    }
    const error = validateCommandCenterComposerStep(activeStep, formData, {
      selectedProviderId,
      selectedOrganizationIds: selectedOrgIds,
      selectedCreator,
      marketValidationError,
    })
    if (error) {
      showStepError(error)
      return
    }
    if (activeStep === 'distribution' && !(await runPreflight())) return
    setStepError(null)
    const nextStep = COMMAND_CENTER_COMPOSER_STEPS[currentStepIndex + 1]
    if (nextStep) setActiveStep(nextStep.value)
  }
  const returnToPreviousStep = () => {
    setStepError(null)
    const previousStep = COMMAND_CENTER_COMPOSER_STEPS[currentStepIndex - 1]
    if (previousStep) setActiveStep(previousStep.value)
  }


  const changeGuidedStep = async (step) => {
    if (step === 'review' && !preflightIsCurrent && !(await runPreflight())) return
    setStepError(null)
    setActiveStep(step)
  }
  const jumpToSection = (step) => {
    setActiveStep(step)
    if (step === 'market') setOnePageBetExpanded(true)
    if (step === 'review') setReviewExpanded(true)
    document.getElementById(`post-composer-section-${step}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }
  const compareWithLatestRevision = async () => {
    setIsComparingRevision(true)
    try {
      const latest = await commandCenterApi.getPostGroup(groupIdParam)
      setLoadedGroupBaseline(latest)
      setPreflightState(null)
      setRevisionConflict({
        currentRevision: latest.revision,
        compared: true,
        message: `Revision ${latest.revision} is now the comparison baseline. Your authored edits are preserved; review every destination again before saving.`,
      })
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setIsComparingRevision(false)
    }
  }
  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) {
      let message = preflightState
        ? 'The post changed after review. Review every destination again before submitting.'
        : 'Review every destination before submitting.'
      if (requiredMetadataPending) {
        message = 'Wait for required book metadata to finish loading before submitting.'
      } else if (requiredMetadataFailed) {
        message = 'Required book metadata could not load. Retry before submitting.'
      }
      showStepError({ step: 'review', message })
      return
    }
    setIsSubmitting(true)
    setRevisionConflict(null)
    try {
      const result = isEditMode
        ? await commandCenterApi.updatePostGroup(groupIdParam, mutationPayload)
        : await commandCenterApi.createPostGroup(mutationPayload)
      // The toast names the books moderation keeps the post out of.
      const saved = postGroupSaveMessage(result, isEditMode)
      if (saved.tone === 'warning') toast.warning(saved.message)
      else toast.success(saved.message)
      if (!isEditMode) removeStoredValue(draftKey)

      queryClient.invalidateQueries({ queryKey: ['cc-post-groups'] })
      queryClient.invalidateQueries({ queryKey: ['cc-post-group', result.group_id] })
      navigate(`/command-center/post-groups/${result.group_id}`, { replace: true })
    } catch (err) {
      if (err.response?.status === 409) {
        setRevisionConflict({
          currentRevision: err.response?.data?.current_revision,
          compared: false,
          message: extractErrorMessage(err),
        })
      }
      toast.error(extractErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <PageLayout size="wide">
        <StatePanel state="loading" title="Loading distributed post" description="The composer will be ready in a moment." />
      </PageLayout>
    )
  }

  const finalActionLabel = isEditMode
    ? 'Save changes'
    : formData.skipApproval
      ? 'Publish distributed post'
      : 'Submit for approval'
  const marketReviewSummary = previewLegPresentations.length > 0
    ? previewLegPresentations.map((leg) => [
      leg.event || 'Event',
      leg.market || 'Market',
      leg.selection || 'Selection',
      formatOdds(leg.odds),
      leg.outcomeId ? `Outcome ID ${leg.outcomeId}` : null,
      leg.betOfferId ? `Bet offer ID ${leg.betOfferId}` : null,
    ].filter(Boolean).join(' · ')).join('; ')
    : 'No market attached'
  const baselineSharedDocument = loadedGroupBaseline?.document?.shared
  const reviewSharedValue = (field, value, format = (candidate) => candidate || 'None') => {
    const proposed = format(value)
    if (!baselineSharedDocument || JSON.stringify(baselineSharedDocument[field]) === JSON.stringify(value)) {
      return proposed
    }
    return `${format(baselineSharedDocument[field])} → ${proposed}`
  }
  const reviewContextItems = [
    { key: 'label', label: 'Internal label', value: reviewSharedValue('label', formData.label || '', (value) => value || 'Untitled distributed post') },
    { key: 'creator', label: 'Creator', value: selectedCreator?.display_name || 'No creator selected' },
    { key: 'provider', label: 'Provider', value: selectedProvider?.name || 'No provider selected' },
    { key: 'market', label: 'Market / price', value: marketReviewSummary },
    { key: 'card-title', label: 'Card title', value: reviewSharedValue('title', formData.title || '', (value) => value || 'Automatic') },
    { key: 'card-subtitle', label: 'Card subtitle', value: reviewSharedValue('subtitle', formData.subtitle || '', (value) => value || 'Automatic') },
    { key: 'copy', label: 'Post text', value: reviewSharedValue('text', formData.text || '', (value) => value || 'No post text') },
    {
      key: 'media',
      label: 'Media',
      value: reviewSharedValue(
        'media_objects',
        formData.mediaObjects,
        (value) => `${value?.length || 0} attachment${value?.length === 1 ? '' : 's'}`,
      ),
    },
  ]
  const reviewedDestinations = preflightMatchesDocument && preflightState.result.destinations?.length > 0
    ? preflightState.result.destinations
    : selectedOrgs.map((organization) => ({
      organization_id: organization.id,
      organization_name: organization.name,
      surface_ids: formData.surfaceIdsMap[organization.id] || [],
      regulation_ids: formData.targetOfferingsMap[organization.id] || [],
      tag_ids: formData.tagIdsMap[organization.id] || [],
      override_fields: Object.keys(formData.bookOverrides?.[organization.id] || {}),
      title: resolveCommandCenterDestinationDocumentV2(mutationPayload, organization.id).title,
      media_objects: resolveCommandCenterDestinationDocumentV2(mutationPayload, organization.id).media_objects,
      capable: false,
      conflicts: [],
    }))
  const destinationTagNames = (organizationId, tagIds = []) => {
    const labels = new Map((orgTags[organizationId] || []).map((tag) => [tag.id, tag.label || tag.id]))
    return (tagIds || []).map((tagId) => labels.get(tagId) || tagId)
  }
  const removedDestinationChanges = (loadedGroupBaseline?.destinations || [])
    .filter((destination) => !selectedOrgIds.includes(destination.organization_id))
    .map((destination) => {
      const org = allOrganizations.find((organization) => organization.id === destination.organization_id)
      const surfaces = orgSurfaces[destination.organization_id] || []
      const surfaceNames = (destination.surface_ids || []).map((surfaceId) => (
        surfaces.find((surface) => surface.id === surfaceId)?.name || surfaceId
      ))
      const tagNames = destinationTagNames(destination.organization_id, destination.tag_ids)
      return {
        key: `removed-${destination.organization_id}`,
        label: destination.organization_name || org?.name || destination.organization_id,
        current: [
          surfaceNames.length ? `Surfaces: ${surfaceNames.join(', ')}` : 'Default organization surfaces',
          tagNames.length ? `Tags: ${tagNames.join(', ')}` : 'No tags',
        ].join(' · '),
        proposed: 'Removed from distributed post',
        impact: 'This member leaves the distributed post when changes are saved.',
      }
    })
  const reviewDestinationChanges = [
    ...reviewedDestinations.map((destination) => {
      const org = selectedOrgs.find((organization) => organization.id === destination.organization_id)
      const surfaces = orgSurfaces[destination.organization_id] || []
      const names = surfaces
        .filter((surface) => (destination.surface_ids || []).includes(surface.id))
        .map((surface) => surface.name || surface.slug)
      const regulationLabels = (destination.regulation_ids || []).map((regulationId) => (
        regulationLabelForOrganization(org, regulationId)
      ))
      const proposedTagNames = destinationTagNames(destination.organization_id, destination.tag_ids)
      const existingDestination = loadedGroupBaseline?.destinations?.find(
        (candidate) => candidate.organization_id === destination.organization_id,
      )
      const existingSurfaceNames = surfaces
        .filter((surface) => (existingDestination?.surface_ids || []).includes(surface.id))
        .map((surface) => surface.name || surface.slug)
      const existingTagNames = destinationTagNames(
        destination.organization_id,
        existingDestination?.tag_ids,
      )
      const overrideFields = (destination.override_fields || []).map((field) => (
        field.replaceAll('_', ' ')
      ))
      const proposed = [
        destination.title ? `Title: ${destination.title}` : 'Automatic title',
        `${destination.media_objects?.length || 0} media attachment${destination.media_objects?.length === 1 ? '' : 's'}`,
        names.length ? `Surfaces: ${names.join(', ')}` : 'No surface selected',
        regulationLabels.length ? `Regulations: ${regulationLabels.join(', ')}` : null,
        proposedTagNames.length ? `Tags: ${proposedTagNames.join(', ')}` : 'No tags',
        overrideFields.length ? `Overrides: ${overrideFields.join(', ')}` : 'Uses shared content',
        destination.moderation_findings?.length ? `Moderation: ${summarizeFindings(destination.moderation_findings)}` : null,
      ].filter(Boolean).join(' · ')
      const conflictMessage = (destination.conflicts || []).map((conflict) => conflict.message).join(' ')

      return {
        key: destination.organization_id,
        label: destination.organization_name || org?.name || 'Organization',
        current: existingDestination
          ? [
              existingSurfaceNames.length ? `Surfaces: ${existingSurfaceNames.join(', ')}` : 'Default organization surfaces',
              existingTagNames.length ? `Tags: ${existingTagNames.join(', ')}` : 'No tags',
            ].join(' · ')
          : 'No member post',
        proposed,
        impact: destination.capable
          ? (formData.skipApproval
            ? 'Ready to save and publish with the group.'
            : 'Ready to save as a pending member with the group.')
          : conflictMessage || 'Run destination review to confirm capability.',
      }
    }),
    ...removedDestinationChanges,
    {
      key: 'approval-state',
      label: 'Approval state',
      current: loadedGroupBaseline?.approval_status?.replaceAll('_', ' ') || 'Not submitted',
      proposed: formData.skipApproval ? 'Approved' : 'Pending approval',
      impact: `Applies to all ${selectedOrgs.length} member post${selectedOrgs.length === 1 ? '' : 's'} together.`,
    },
  ]

  const booksSection = (
        <FormSection>
          {providersQuery.isError || organizationsQuery.isError ? (
            <StatePanel
              compact
              state="error"
              title="Provider and book metadata could not load"
              description="Retry before selecting destinations or reviewing this post."
              onRetry={() => {
                providersQuery.refetch()
                organizationsQuery.refetch()
              }}
            />
          ) : null}
          <FormField label="Books" required>
            <div className={styles.providerTree}>
              {visibleProviders.map((provider) => {
                const providerOrganizations = organizationsByProvider.get(provider.id) || []
                // A provider with no receivable books is unactionable noise
                // (still shown while loading or when it owns the loaded group).
                if (
                  providerOrganizations.length === 0
                  && organizationsQuery.isSuccess
                  && provider.id !== selectedProviderId
                ) return null
                const selectedCount = provider.id === selectedProviderId
                  ? providerOrganizations.filter((organization) => selectedOrgIds.includes(organization.id)).length
                  : 0
                const allSelected = providerOrganizations.length > 0 && selectedCount === providerOrganizations.length
                const someSelected = selectedCount > 0 && !allSelected
                const providerDisabled = isEditMode && selectedProviderId !== provider.id
                return (
                  <fieldset key={provider.id} className={styles.providerBranch} disabled={providerDisabled}>
                    <legend>
                      <label className={`${styles.providerRow} ${selectedProviderId === provider.id ? styles.optionSelected : ''}`}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          aria-checked={someSelected ? 'mixed' : allSelected}
                          ref={(input) => {
                            if (input) input.indeterminate = someSelected
                          }}
                          onChange={() => toggleProviderOrganizations(provider.id)}
                        />
                        <span>
                          <strong>{provider.name}</strong>
                          <em>{selectedCount} of {providerOrganizations.length} books selected</em>
                        </span>
                      </label>
                    </legend>
                    {providerOrganizations.length === 0 ? (
                      <p className={composerStyles.emptyNote}>No available books.</p>
                    ) : (
                      <div className={styles.bookTree}>
                        {providerOrganizations.map((organization) => {
                          const selected = selectedProviderId === provider.id && selectedOrgIds.includes(organization.id)
                          const regulationCount = organization.kambi_offering_ids.length
                          return (
                            <label
                              key={organization.id}
                              className={`${styles.bookRow} ${selected ? styles.optionSelected : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => selectOrganization(provider.id, organization.id)}
                              />
                              <span>
                                <strong>{organization.name}</strong>
                                <em>{regulationCount} regulation{regulationCount === 1 ? '' : 's'}</em>
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </fieldset>
                )
              })}
            </div>
          </FormField>
        </FormSection>
  )
  // One tab per selected book. Built outside the JSX so the pane body reads
  // at its own indentation instead of nested six levels inside a prop.
  const destinationBookTabs = selectedOrgs.map((org) => {
    const surfaces = orgSurfaces[org.id] || []
    const defaultSurfaceIds = surfaces.filter((surface) => surface.include_by_default).map((surface) => surface.id)
    const selectedSurfaceIds = Array.isArray(formData.surfaceIdsMap[org.id])
      ? formData.surfaceIdsMap[org.id]
      : defaultSurfaceIds
    const offerings = Array.isArray(org.kambi_offering_ids) ? org.kambi_offering_ids : []
    const selectedOfferings = formData.targetOfferingsMap[org.id] || []
    const tags = orgTags[org.id] || []
    const selectedTags = formData.tagIdsMap[org.id] || []
    const bookOverrides = formData.bookOverrides?.[org.id] || {}
    const overrideCount = Object.keys(bookOverrides).length
    const mediaOverride = bookOverrides.media_objects
    const ctaOverride = bookOverrides.cta
    const isTabActive = org.id === activeDestinationTabValue

    return {
      value: org.id,
      label: org.name,
      // Surfaces the one thing an operator would otherwise have to
      // open every tab to discover.
      count: overrideCount || undefined,
      panel: (
        <section className={styles.orgSettingsCard} aria-label={`${org.name} destinations`}>
          <div className={styles.orgSettingsGroup}>
            {surfaces.length === 0 ? (
              <FormSection title={PUBLISH_TO_TITLE} headingSize="subsection" description={PUBLISH_TO_DESCRIPTION}>
                <StatePanel compact state="disabled" title="No feed surfaces configured" />
              </FormSection>
            ) : (
              <FormSection title={PUBLISH_TO_TITLE} headingSize="subsection" description={PUBLISH_TO_DESCRIPTION}>
                <SurfacePicker
                  surfaces={surfaces}
                  selectedIds={selectedSurfaceIds}
                  onToggle={(surfaceId) => toggleSurface(org.id, surfaceId)}
                  testId={`publish-to-chips-${org.slug}`}
                />
              </FormSection>
            )}
          </div>

          {isKambi && offerings.length > 0 ? (
            <div className={styles.orgSettingsGroup}>
              <RegulationTargetSection
                regulations={offerings.map((offeringId) => ({
                  value: offeringId,
                  label: regulationLabelForOrganization(org, offeringId),
                }))}
                selected={selectedOfferings}
                onToggle={(offeringId) => toggleOffering(org.id, offeringId)}
                onSelectAll={() => setOfferingsForOrganization(org.id, offerings)}
                onClear={() => setOfferingsForOrganization(org.id, [])}
                /* Command Center preflight rejects a book with no
                   regulations, so the single-book "runs everywhere"
                   reading does not apply here. */
                unrestrictedWhenEmpty={false}
              />
            </div>
          ) : null}

          <div className={styles.orgSettingsGroup}>
            <strong>Tags</strong>
            <TagChipPicker
              tags={tags}
              selectedTagIds={selectedTags}
              onToggle={(tagId) => toggleTag(org.id, tagId)}
            />
          </div>

          <details className={styles.overridePanel} open={overrideCount > 0}>
            <summary>Book-specific content</summary>
            {[
              ['title', 'Title', formData.title],
              ['subtitle', 'Subtitle', formData.subtitle],
            ].map(([field, label, sharedValue]) => (
              <div key={field} className={styles.overrideField}>
                <Toggle
                  label={`Override ${label.toLowerCase()}`}
                  checked={Boolean(bookOverrides[field])}
                  onChange={(enabled) => (
                    enabled
                      ? setBookOverride(org.id, field, sharedValue || '')
                      : clearBookOverride(org.id, field)
                  )}
                />
                {bookOverrides[field] ? (
                  <input
                    className={styles.textInput}
                    aria-label={`${org.name} ${label.toLowerCase()} override`}
                    value={bookOverrides[field].value || ''}
                    onChange={(event) => setBookOverride(org.id, field, event.target.value)}
                  />
                ) : null}
              </div>
            ))}

            <div className={styles.overrideField}>
              <Toggle
                label="Override post text"
                checked={Boolean(bookOverrides.text)}
                onChange={(enabled) => (
                  enabled
                    ? setBookOverride(org.id, 'text', formData.text || '')
                    : clearBookOverride(org.id, 'text')
                )}
              />
              {bookOverrides.text ? (
                <textarea
                  className={styles.textArea}
                  aria-label={`${org.name} post text override`}
                  value={bookOverrides.text.value || ''}
                  onChange={(event) => setBookOverride(org.id, 'text', event.target.value)}
                />
              ) : null}
            </div>

            <div className={styles.overrideField}>
              <Toggle
                label="Override call to action"
                checked={Boolean(ctaOverride)}
                onChange={(enabled) => (
                  enabled
                    ? setBookOverride(org.id, 'cta', mutationPayload.document.shared.cta)
                    : clearBookOverride(org.id, 'cta')
                )}
              />
              {ctaOverride ? (
                <div className={styles.overrideInputGrid}>
                  <select
                    className={styles.select}
                    aria-label={`${org.name} call to action override`}
                    value={ctaOverride.value?.kind || 'none'}
                    onChange={(event) => {
                      const kind = event.target.value
                      setBookOverride(
                        org.id,
                        'cta',
                        kind === 'none'
                          ? null
                          : { kind, label: ctaOverride.value?.label || '', target: ctaOverride.value?.target || '' },
                      )
                    }}
                  >
                    <option value="none">No call to action</option>
                    <option value="betslip">Add to betslip</option>
                    <option value="link">Open link</option>
                    <option value="provider-cart">Provider cart</option>
                  </select>
                  {ctaOverride.value ? (
                    <>
                      <input
                        className={styles.textInput}
                        aria-label={`${org.name} call to action label`}
                        placeholder="Button label"
                        value={ctaOverride.value.label || ''}
                        onChange={(event) => setBookOverride(org.id, 'cta', {
                          ...ctaOverride.value,
                          label: event.target.value,
                        })}
                      />
                      <input
                        className={styles.textInput}
                        aria-label={`${org.name} call to action target`}
                        placeholder="Target"
                        value={ctaOverride.value.target || ''}
                        onChange={(event) => setBookOverride(org.id, 'cta', {
                          ...ctaOverride.value,
                          target: event.target.value,
                        })}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className={styles.overrideField}>
              <Toggle
                label="Override media"
                checked={Boolean(mediaOverride)}
                onChange={(enabled) => (
                  enabled
                    ? setBookOverride(org.id, 'media_objects', [])
                    : clearBookOverride(org.id, 'media_objects')
                )}
              />
              {mediaOverride && isTabActive ? (
                <OrgMediaOverridePicker
                  org={org}
                  selectedMedia={mediaOverride.value || []}
                  onToggleAsset={(asset) => toggleBookMediaAsset(org.id, asset)}
                />
              ) : null}
            </div>
          </details>
        </section>
      ),
    }
  })

  // A chip toggles the preview book's matching tag; applying to all matches
  // each destination against its own saved tags.
  const tagSuggestionChips = showTagSuggestionChips ? (
    <SuggestedTagChips
      suggestions={tagSuggestions}
      tags={previewOrganizationTags}
      selectedTagIds={previewOrganization ? formData.tagIdsMap[previewOrganization.id] || EMPTY_ARRAY : EMPTY_ARRAY}
      onToggleTag={(tagId) => {
        if (previewOrganization) toggleTag(previewOrganization.id, tagId)
      }}
      pending={suggestTagsMutation.isPending}
      title={selectedOrgs.length > 1 && previewOrganization ? `Suggested tags for ${previewOrganization.name}` : 'Suggested tags'}
      actions={selectedOrgs.length > 1 ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={applySuggestedTagsToEveryBook}
          // A book added after the suggestions has no catalog yet.
          disabled={destinationMetadataPending}
        >
          Apply to every selected book
        </Button>
      ) : null}
    />
  ) : null

  const composerSections = {
    content: (
      <div className={styles.stepStack}>
        <MediaSection
          uploadedMedia={uploadedMedia}
          onMediaUpload={handleMediaUpload}
          onRetryUpload={mediaUploads.retry}
          onCancelUpload={mediaUploads.cancel}
          onRemoveMedia={removeMedia}
          libraryAssets={sharedLibraryAssets}
          assetSearch={assetSearch}
          onAssetSearchChange={setAssetSearch}
          onAttachAsset={attachAssetToComposer}
          imageGenerationCapabilities={imageGenerationCapabilities}
          hasCaptionText={formData.text.trim().length > 0}
          imagePrompt={imagePrompt}
          onImagePromptChange={setImagePrompt}
          imagePending={generateImageMutation.isPending}
          imageStatus={imageGenerationJob}
          onGenerateImage={(imageSize) => generateImageMutation.mutate(imageSize)}
          onDiscardImage={generateImageMutation.discardJob}
          referenceImages={referenceImageState.items}
          onAddReferenceImages={referenceImageState.addFiles}
          onRemoveReferenceImage={referenceImageState.removeReference}
          referenceImagesUploading={referenceImageState.uploading}
          aiReview={aiReview}
          onApplyCaption={handleApplyCaption}
          // Tag suggestions render beside Suggest tags instead.
          showTagSuggestionsInAnalysis={false}
          orgSlug={previewOrganization?.slug}
          onExternalCaptionApply={handleExternalCaptionApply}
          ingestApi={{
            start: commandCenterApi.startOrgMediaIngest,
            getStatus: commandCenterApi.getOrgMediaIngestStatus,
          }}
          isCryptoDotCom={isPredictionContractProvider}
          transcriptionJobs={transcriptionJobs}
          onRetryTranscription={handleRetryTranscription}
        />
        {composerMode === 'guided' ? booksSection : null}

        {mediaOwnershipError ? (
          <StatePanel
            compact
            state="error"
            title="Attached media has no selected owner"
            description={mediaOwnershipError}
          />
        ) : null}

        <ContentSection
          text={formData.text}
          onTextChange={(value) => setField('text', value)}
          title={formData.title}
          subtitle={formData.subtitle}
          onTitleChange={(value) => setField('title', value)}
          onSubtitleChange={(value) => setField('subtitle', value)}
          collapseMoreOptions
          moreOptionsAttention={Boolean(
            formData.title
            || formData.subtitle
            || suggestTagsMutation.isPending
          )}
          moreOptionsRevealKey={tagSuggestionsRevealKey}
          moreOptionsNote={tagSuggestionsNote}
          showSectionTitle={false}
          creatorSlot={(
            <div className={styles.creatorPicker}>
              <FormField label="Creator" required>
                <input
                  aria-label="Search creators"
                  type="search"
                  required={false}
                  placeholder="Search creators…"
                  value={creatorSearch}
                  onChange={(event) => setCreatorSearch(event.target.value)}
                  className={styles.select}
                />
              </FormField>
              {creatorsQuery.isError ? (
                <StatePanel
                  compact
                  state="error"
                  title="Creator metadata could not load"
                  description="Retry before reviewing this post."
                  onRetry={() => creatorsQuery.refetch()}
                />
              ) : null}
              <div className={styles.creatorList} aria-label="Available creators">
                {availableCreators.map((creator) => (
                  <button
                    type="button"
                    key={creator.id}
                    className={`${styles.creatorCard} ${selectedCreator?.id === creator.id ? styles.optionSelected : ''}`}
                    aria-pressed={selectedCreator?.id === creator.id}
                    onClick={() => setSelectedCreator(normalizeSelectedCreator(creator))}
                  >
                    {creator.avatar_url ? (
                      <img src={creator.avatar_url} alt="" className={styles.avatar} />
                    ) : (
                      <span className={styles.avatarPlaceholder}>{creator.display_name?.[0]?.toUpperCase() || '?'}</span>
                    )}
                    <span>{creator.display_name}</span>
                  </button>
                ))}
                {availableCreators.length === 0 ? (
                  <p className={composerStyles.emptyNote}>No creators found for the selected organizations.</p>
                ) : null}
              </div>
            </div>
          )}
          labelSlot={(
            <FormField
              label="Label (internal)"
              value={formData.label}
              onChange={(event) => setField('label', event.target.value)}
              placeholder="e.g., Lakers vs Celtics promo"
            />
          )}
          captionAction={{
            onClick: (direction) => runTextAiAction(captionSuggestionReason, suggestCaptionMutation, direction),
            disabled: Boolean(captionSuggestionReason) || suggestCaptionMutation.isPending,
            pending: suggestCaptionMutation.isPending,
            reason: captionSuggestionReason,
          }}
          captionTools={!captionSuggestionReason || aiReview.captions?.length ? {
            guidance: captionGuidance,
            onGuidanceChange: setCaptionGuidance,
            suggestions: aiReview.captions?.length ? aiReview.captions : (aiReview.caption ? [aiReview.caption] : []),
            onUseSuggestion: handleExternalCaptionApply,
            transcriptionPending: Object.values(transcriptionJobs).some((job) => job?.status === 'polling' || job?.status === 'retrying'),
          } : null}
          tagsAction={{
            onClick: () => runTextAiAction(tagsSuggestionReason, suggestTagsMutation),
            disabled: Boolean(tagsSuggestionReason) || suggestTagsMutation.isPending,
            pending: suggestTagsMutation.isPending,
            reason: tagsSuggestionReason,
          }}
          tagsSuggestionsSlot={tagSuggestionChips}
        />
      </div>
    ),
    market: (
      <BetSection
        isKambi={isKambi}
        isCryptoDotCom={isCryptoDotCom}
        isOG={isOG}
        outcomeIds={formData.kambiOutcomeIds}
        targetOfferings={Object.values(formData.targetOfferingsMap).flat()}
        allOfferings={kambiAllOfferings}
        providerId={selectedProviderId}
        destinations={kambiDestinations}
        onOutcomeIdsChange={setKambiOutcomeIds}
        // Shown, disabled, before any book is picked: Bets sits above Books,
        // so the search box is where operators look first.
        marketSearch={isKambi || !selectedProvider ? {
          scope: commandCenterLegSearchScope(selectedProviderId, kambiDestinations),
          search: createCommandCenterLegSearch(selectedProviderId, kambiDestinations),
          offerings: commandCenterLegSearchOfferings(kambiDestinations),
          query: marketSearchQuery,
          onQueryChange: setMarketSearchQuery,
          onSelect: handleSelectKambiSearchResult,
          // Search can miss an outcome or be down; Kambi validation checks a
          // typed ID on every destination without it.
          onAddOutcomeId: addKambiOutcomeId,
          enabled: isKambi && kambiDestinations.length > 0,
          // Search covers each book's first regulation, not every selected
          // one; validation reports availability per book once a bet is added.
          coverage: false,
          help: !selectedProvider
            ? 'Select books below to search markets.'
            : kambiDestinations.length > 0
              ? 'Search by player, team, event, market, or outcome ID. Prices and availability are rechecked for each book before publish.'
              : 'Select books and regulations below to search markets.',
        } : null}
        unavailableBetMessage="Bets cannot be attached for this provider."
        onValidationResult={handleKambiValidationResult}
        wagerEntries={formData.wagerEntries}
        onWagerEntriesChange={(entries) => setField('wagerEntries', entries)}
        wagerValidationError={authoredWagerValidationError}
        validationResult={kambiValidation}
        selectedCreator={selectedCreator}
        destinationOrganizations={selectedOrgs}
        providerSelectionIds={formData.providerSelectionIds}
        onProviderSelectionIdsChange={(ids) => handleProviderSelectionIdsChange(ids)}
        onSelectedProviderSelectionsChange={(selections) => handleProviderSelectionIdsChange(
          selections.map((selection) => selection.external_id).filter(Boolean),
          selections,
        )}
        extractBetDetailsAction={{
          onClick: () => runTextAiAction(betDetailsReason, extractBetDetailsMutation),
          disabled: Boolean(betDetailsReason) || extractBetDetailsMutation.isPending,
          pending: extractBetDetailsMutation.isPending,
          reason: betDetailsReason,
        }}
      />
    ),
    distribution: (
      <div className={styles.stepStack} data-testid="publish-to-section">
        <FormSection title="Destinations by book">
          {destinationMetadataPending ? (
            <StatePanel compact state="loading" title="Loading destination settings" />
          ) : destinationMetadata.errors.length > 0 ? (
            <StatePanel
              compact
              state="error"
              title="Destination settings could not load"
              description={`Retry ${destinationMetadata.errors.join(', ')} before reviewing this post.`}
              onRetry={() => setDestinationMetadataRetry((value) => value + 1)}
            />
          ) : null}
          {selectedOrgs.length === 0 ? (
            <StatePanel
              compact
              state="empty"
              title="No books selected"
              description="Choose the books this post should reach before setting their destinations."
            />
          ) : (
            <>
              {/* The Books picker lives in the Content step (guided) or just
                  above this one (one page), so name the current scope here
                  rather than leaving the step without context. */}
              <p className={styles.destinationScopeSummary}>
                {selectedOrgs.length} book{selectedOrgs.length === 1 ? '' : 's'} selected.
                Each book below publishes on its own surfaces and regulations.
              </p>
              <Tabs
                label="Destination book"
                className={styles.destinationTabs}
                value={activeDestinationTabValue}
                onValueChange={setActiveDestinationOrgId}
                /* An operator part-way through typing an override must not lose
                   it by glancing at another book. */
                keepMounted
                items={destinationBookTabs}
              />
            </>
          )}
        </FormSection>

        <AdvancedSettingsSection
          values={formData}
          isEditMode={isEditMode}
          expanded={advancedSectionExpanded}
          onToggle={setAdvancedSectionExpanded}
          onChange={setField}
          onToggleChange={setField}
          ctaChildren={(
            <CtaSection
              embedded
              isCryptoDotCom={isCryptoDotCom}
              isOG={isOG}
              ctaKind={formData.ctaKind}
              ctaLabel={formData.ctaLabel}
              ctaTarget={formData.ctaTarget}
              onChange={setField}
            />
          )}
        />
      </div>
    ),
    review: (
      <div
        id="post-composer-section-review"
        className={`${styles.reviewStack} ${composerMode === 'one-page' ? composerStyles.onePageSection : ''}`}
      >
        <FormSection
          title="Review"
          headingSize="subsection"
          collapsible={composerMode === 'one-page'}
          expanded={composerMode !== 'one-page' || reviewExpanded || preflightIsCurrent || stepError?.step === 'review'}
          onToggle={setReviewExpanded}
        >
          <StatePanel
            compact
            state={preflightIsCurrent ? 'success' : stepError?.step === 'review' ? 'error' : 'empty'}
            title={preflightIsCurrent ? 'Every destination is ready' : 'Review destinations to continue'}
            description={preflightIsCurrent
              ? `${preflightState.result.destinations.length} book${preflightState.result.destinations.length === 1 ? '' : 's'} passed provider, ownership, and destination checks.`
              : undefined}
          />
          {!isEditMode ? (
            <div className={styles.instantPostingChoice}>
              <Toggle
                label="Post instantly"
                checked={formData.skipApproval}
                onChange={(value) => setField('skipApproval', value)}
              />
              <p>
                {formData.skipApproval
                  ? 'Publishes to every reviewed destination without another approval.'
                  : 'Sends every destination to the approval queue.'}
              </p>
            </div>
          ) : null}
          <DecisionReview
            title="Review distributed post"
            contextTitle="Shared content and market"
            contextItems={reviewContextItems}
            changeTitle="Effective book results"
            changeItems={reviewDestinationChanges}
            consequenceTitle={formData.skipApproval ? 'Publish immediately' : 'Submit for approval'}
            consequence={formData.skipApproval
              ? `Publishes ${selectedOrgs.length} member post${selectedOrgs.length === 1 ? '' : 's'} now.`
              : `Creates ${selectedOrgs.length} pending member post${selectedOrgs.length === 1 ? '' : 's'}. An authorized reviewer must approve the group before feed updates are queued.`}
          />
        </FormSection>
      </div>
    ),
  }
  const renderBackActions = (allowStepBack) => (
    <div className={styles.stepBackActions}>
      <Button
        ref={mobilePreviewTriggerRef}
        type="button"
        variant="secondary"
        className={`${composerStyles.mobilePreviewAction} ${styles.composerPreviewAction}`}
        onClick={() => setMobilePreviewOpen(true)}
      >
        Preview
      </Button>
      {allowStepBack && currentStepIndex > 0 ? (
        <Button type="button" variant="secondary" onClick={returnToPreviousStep}>Back</Button>
      ) : (
        <Button type="button" variant="secondary" onClick={() => navigate('/command-center/posts')}>Cancel</Button>
      )}
    </div>
  )
  const reviewAction = preflightIsCurrent ? (
    <Button type="submit" loading={isSubmitting} disabled={!canSubmit}>{finalActionLabel}</Button>
  ) : (
    <Button type="button" loading={isPreflighting} disabled={!canReview} onClick={runPreflight}>
      Review destinations
    </Button>
  )
  const guidedAction = activeStep === 'review' ? reviewAction : (
    <Button type="button" loading={activeStep === 'distribution' && isPreflighting} onClick={continueToNextStep}>
      {activeStep === 'distribution' ? 'Review destinations' : 'Continue'}
    </Button>
  )


  return (
    <PostComposerLayout
      size="full"
      title={isEditMode ? 'Edit distributed post' : 'Create post'}
      breadcrumbs={<Link className={styles.backLink} to="/command-center/posts">Distributed posts</Link>}
      actions={(
        <SegmentedControl
          label="Composer layout"
          value={composerMode}
          onValueChange={changeComposerMode}
          items={[
            { value: 'guided', label: 'Guided' },
            { value: 'one-page', label: 'One page' },
          ]}
        />
      )}
      draftBanner={pendingDraft ? (
        <div className={composerStyles.draftBanner} role="status">
          <div>
            <strong>Resume your last draft?</strong>
            <p>We found a saved draft from {new Date(pendingDraft.savedAt).toLocaleString()}.</p>
          </div>
          <div className={composerStyles.draftBannerActions}>
            <Button type="button" variant="secondary" onClick={discardDraft}>Discard</Button>
            <Button type="button" onClick={restoreDraft}>Restore draft</Button>
          </div>
        </div>
      ) : null}
      steps={composerMode === 'guided'
        ? COMMAND_CENTER_COMPOSER_STEPS.map((step, index) => ({
          ...step,
          complete: index < currentStepIndex,
        }))
        : undefined}
      currentStep={activeStep}
      onStepChange={changeGuidedStep}
      stepperClassName={styles.commandStepper}
      editor={(
        <form
          data-testid="command-center-composer-scroll"
          onSubmit={handleSubmit}
          onKeyDown={blockImplicitFormSubmit}
          className={`${composerStyles.form} ${composerStyles.boundedMobileForm} ${styles.composerForm} ${composerMode === 'one-page' ? composerStyles.onePageComposer : ''}`}
        >
          <div className={styles.composerScrollBody}>
            {composerMode === 'one-page' ? (
              <nav className={composerStyles.sectionJumpBar} aria-label="Post sections">
                {COMMAND_CENTER_SECTION_REGISTRY.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={composerStyles.sectionJumpBtn}
                    aria-current={activeStep === entry.id ? 'page' : undefined}
                    onClick={() => jumpToSection(entry.id)}
                  >
                    {sectionLabel(entry, 'commandCenter')}
                  </button>
                ))}
              </nav>
            ) : null}

            {stepError ? (
              <ErrorSummary ref={errorSummaryRef} title="Complete this step before continuing">
                <p>{stepError.message}</p>
              </ErrorSummary>
            ) : null}
            {revisionConflict ? (
              <StatePanel
                state="error"
                title="A newer revision is available"
                description={revisionConflict.message}
              >
                <div className={styles.conflictActions}>
                  <Button
                    type="button"
                    variant="secondary"
                    loading={isComparingRevision}
                    onClick={compareWithLatestRevision}
                  >
                    Compare with latest
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
                    Reload latest
                  </Button>
                </div>
              </StatePanel>
            ) : null}

            {renderComposerSections({
              registry: COMMAND_CENTER_SECTION_REGISTRY,
              mode: composerMode,
              activeStep,
              sections: composerSections,
              marketExpanded: onePageBetExpanded,
              onToggleMarketExpanded: setOnePageBetExpanded,
              afterSections: { market: booksSection },
            })}
          </div>

          {composerMode === 'guided' ? (
            <div className={styles.guidedActions}>
              {renderBackActions(true)}
              {guidedAction}
            </div>
          ) : (
            <div className={styles.onePageActions}>
              {renderBackActions(false)}
              {reviewAction}
            </div>
          )}
        </form>
      )}
      preview={(
        <div className={styles.previewStack}>
          {selectedOrgs.length > 1 ? (
            <div className={styles.previewTabs} role="tablist" aria-label="Preview book">
              {selectedOrgs.map((organization) => (
                <button
                  key={organization.id}
                  type="button"
                  role="tab"
                  aria-selected={previewOrganization?.id === organization.id}
                  className={previewOrganization?.id === organization.id ? styles.previewTabActive : styles.previewTab}
                  onClick={() => setPreviewOrganizationId(organization.id)}
                >
                  {organization.name}
                </button>
              ))}
            </div>
          ) : null}
          <PostPreviewPanel
            branding={brandingData}
            item={previewPayload}
            mobileOpen={mobilePreviewOpen}
            onMobileOpen={() => setMobilePreviewOpen(true)}
            onMobileClose={() => setMobilePreviewOpen(false)}
            mobileTriggerRef={mobilePreviewTriggerRef}
            testId="command-center-post-creator-preview"
          />
        </div>
      )}
    />
  )
}

export default CommandCenterPostCreator
